/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React from "react";
import Status from "../Status/Status";
import Options from "../Option/Options";
import StatusIcon from "../Status/StatusIcon";
import { GridColDef, GridColumnHeaderParams } from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
import { DATEFORMATS, DEVICESTATUS, statusRNColumns } from "../../../globals/globals";
import { IColumnWithChild } from "./types";
import { Box, Chip, IconButton, Link, Tooltip, Typography } from "@mui/material";
import { Checkbox } from "../../ui/checkbox";
import { Switch } from "../../ui/switch";
import {
  Tooltip as CustomTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";
import { Badge } from "../../ui/badge";
import MapIcon from "@mui/icons-material/Map";
import { Link as RouterLink } from "react-router-dom";
import LANDeviceActionMenu from "../../Configuration/IPInterface/LANDeviceActionMenu";
import IPInterfaceActionMenu from "../../Configuration/IPInterface/IPInterfaceActionMenu";
import LANDeviceStatus from "../../Configuration/IPInterface/LANDeviceStatus";
import AlarmsStatus from "../../../pages/Nodes/NodeDisplay/AlarmsStatus";
import ViolationsDrawerWrapper from "../../Drawers/ViolationsDrawerWrapper";
import ExportTemplate from "../../../pages/Tasks/ExportTemplate";
import RemoveTemplate from "../../../pages/Tasks/RemoveTemplate";

import FileSelect from "../FileImport/FileApply";
import { convertSymbols, handleBooleanStringValues, formatPercent, formatCounts, formatBytes, convertEpochToGMT, sanitizeHtmlInput, formatUtcTimestamp, capitalizeWords } from "../../../utils/helpers";
import DeleteRowFromSetDebug from "../../Tasks/TaskList/SetDebugLevel/DeleteRowFromSetDebug";
import { moduleGroups, levelOptions } from "../../Tasks/TaskList/SetDebugLevel/moduleGroup";
import ActionItems from "../../Administrations/FileManagements/Users/ActionItems";
import { fileMgmtModuleList, constStr, CellKpiIconState, ExternalPerformanceIconState } from "../../../globals/constants";
import { ProgressBar } from "../ProgressBar/ProgressBar";
import { IRadioNodesDataGridRow, IEventConfig } from "../../../store/api/deviceConfig/types";
import DownloadReport from "../../../pages/Nodes/Reports/DownloadReport";
import DeleteReport from "../../../pages/Nodes/Reports/DeleteReport";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import DownloadFile from "../../Configuration/Files/DownloadFile";
import CellKpiStatus from "../Status/CellKpiStatus";
import ExternalActionItems from "../../Administrations/FileManagements/ExternalUsers/ExternalActionItems";
import ProfileActionMenu from "../../Administrations/PerformanceSettings/ThresholdCrossingSettings/ProfileActionMenu";
import ProfileRuleActionMenu from "../../Administrations/PerformanceSettings/ThresholdCrossingSettings/ProfileRuleActionMenu";
import ThresholdCrossingClearButton from "../../Nodes/PerformanceDisplay/ThresholdCrossing/ThresholdCrossingClearButton";
import TerminalIcon from '@mui/icons-material/Terminal';
import { isDasModel } from "../../../pages/Configuration/helpers";
import { getBaseDomain } from "../../../utils/cookie";
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import AddCommentTwoToneIcon from '@mui/icons-material/AddCommentTwoTone';
import EditIcon from "@mui/icons-material/Edit";
import GoldenDefinitionActionsMenu from "../../../pages/Tasks/GoldenDefinitionActionsMenu";
import StarIcon from "@mui/icons-material/Star";
import { Icon } from "../Icon";
import DifferenceIcon from '@mui/icons-material/Difference';
import { IUser } from '../../../store/api/admin/types';
import { MoreVertical } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../ui/dropdown-menu';
import { useSelector } from "react-redux";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import { prepareActionProps } from "../../Actions/ActionHelper";
import { nodeDisplayOptions } from "../../Actions/ActionOptions";
import Actions from "../../Actions/Actions";

const NodeGridActionsCell: React.FC<{ row: any }> = ({ row }) => {
  const permissions = useSelector(selectProfilePermissionsSelector);

  const actionProps = React.useMemo(
    () => prepareActionProps(row.objectKey, row.name || "", row, nodeDisplayOptions, "nodeDisplay", false, permissions),
    [row, permissions],
  );

  return (
    <div onClick={(event) => event.stopPropagation()}>
      <Actions actionProps={actionProps} size="small" tableId="deviceRow" triggerType="gridActionMenu" />
    </div>
  );
};

export const nodesColumns: GridColDef[] = [
  {
    field: "geoRegionName",
    headerName: "Region",
    flex: 1,
    minWidth: 120,
    renderCell: (cellValues: any) => {
      return <span className="text-sm text-muted-foreground">{cellValues.value}</span>;
    }
  },
  {
    field: "name",
    headerName: "Device",
    flex: 2,
    minWidth: 180,
    renderCell: (cellValues: any) => {
      const display = cellValues.row.modelName === "migration" ? cellValues.row.objectKey : cellValues.row.name;
      return (
        <span className="group/devlink inline-flex items-center gap-1 min-w-0 text-link cursor-pointer">
          <span className="font-medium truncate group-hover/devlink:underline">{display}</span>
          <Icon
            name="open_in_new"
            size={14}
            className="shrink-0 opacity-0 group-hover/devlink:opacity-70 transition-opacity text-muted-foreground"
          />
        </span>
      );
    },
  },
  {
    field: "nodeType",
    headerName: "Type",
    flex: 1,
    minWidth: 100,
    renderCell: (cellValues: any) => {
      const isDas = isDasModel(cellValues.row.modelName);
      const displayType = isDas ? cellValues.row.modelName : cellValues.row.nodeType;
      return (
        <Chip
          size="small"
          label={displayType}
          sx={{
            backgroundColor: "var(--secondary)",
          color: "var(--secondary-foreground)",
          border: "none",
          fontWeight: "var(--font-weight-medium, 500)",
          fontSize: "var(--font-size-xs)",
          height: 26,
          borderRadius: "calc(var(--radius) - 2px)",
          "& .MuiChip-label": { px: 3 },
        }}
      />
    )
  }
  },
  {
    field: "lastestNote",
    headerName: "",
    width: 70,
    sortable: false,
    renderCell: (cellValues: any) => {
      const handleClick = () => {
        const deviceKey = cellValues?.row?.objectKey;
        if (deviceKey && typeof window !== "undefined" && window.sessionStorage) {
          window.sessionStorage.setItem("openNotesForDevice", String(deviceKey));
        }
      };
      return cellValues?.row?.lastestNote ? (
        <span onClick={handleClick} style={{ cursor: "pointer" }}>
          <Icon name="note" size={20} className="text-primary" />
        </span>
      ) : (
        <span onClick={handleClick} style={{ cursor: "pointer" }}>
          <Icon name="note_add" size={20} className="text-muted-foreground opacity-40" />
        </span>
      );
    },
  },
  {
    field: "status",
    headerName: "Status",
    width: 120,
    minWidth: 100,
    renderCell: (cellValues: any) => {
      return <StatusIcon status={cellValues.row.status.toString() === DEVICESTATUS.TIMEOUT ? "Disconnected" : "Connected"} />;
    },
  },
  {
    field: "alarms",
    headerName: "Alarms",
    flex: 1,
    minWidth: 160,
    renderCell: (cellValues: any) => {
      return <AlarmsStatus tableRow={cellValues.row} />;
    },
    sortable: false,
  },
  {
    field: "configurationStatus",
    headerName: "Configuration Status",
    flex: 1,
    minWidth: 140,
    renderCell: (cellValues: any) => {
       return cellValues?.row?.configurationStatus?.trim().toLowerCase() === "na" ? "NA" : capitalizeWords(cellValues?.row?.configurationStatus);
    },
  },
  { field: "goldenAuditFailCount", headerName: "Config Mismatch", flex: 1, minWidth: 120, renderCell: GoldenConfigResultCell },
  { field: "swVersion", headerName: "Version", flex: 1, minWidth: 100 },
  { field: "serialNumber", headerName: "Serial Number", flex: 1, minWidth: 120 },
  {
    field: "ipAddress",
    headerName: "IP Address",
    flex: 1,
    minWidth: 140,
    renderCell: (cellValues: any) => {
      return (
        <>
          {
            (isDasModel(cellValues.row.modelName) || cellValues.row.configurationStatus !== "OFFLINE") &&
            <IconButton
              data-action="sshTerminal"
              aria-label="action"
              component="label"
              size="large"
              sx={{ p: 0, verticalAlign: "middle", color: "var(--primary)" }}
              onClick={(e) => {
                const ip = cellValues.row.ipAddress.replace(/^\[|\]$/g, "");
                if (isDasModel(cellValues.row.modelName)) {
                  const deviceId = cellValues.row.objectKey;
                  const baseDomain = getBaseDomain();
                  // Route the DAS GUI through the auth-gated proxy-<deviceId> subdomain so
                  // the device IP/port is never exposed. Fall back to a direct link when
                  // there is no usable base domain (e.g. IP/localhost dev hosts).
                  if (baseDomain && deviceId) {
                    window.open(`${window.location.protocol}//proxy-${deviceId}.${baseDomain}/`, "_blank");
                  } else {
                    window.open("https://" + ip, "_blank");
                  }
                } else {
                  window.open("/ssh/login/" + ip, "_blank");
                }
                e.stopPropagation();
              }
              }>
              <Icon name="terminal" size={16} className="text-primary" />
            </IconButton>
          }
          <Typography sx={{ ml: 2, fontWeight: "normal" }}>
            {cellValues.row.ipAddress}
          </Typography>
        </>
      );
    },
  },
  {
    field: "pmCollected",
    headerName: "KPI Sync Status",
    flex: 1,
    minWidth: 120,
    renderCell: (cellValues: any) => {
      return (
        <span className="inline-flex items-center gap-2" style={{ fontSize: "var(--font-size-sm)" }}>
          <span className="shrink-0">
            {
              isDasModel(cellValues.row.modelName) ? "NA" : 
            (cellValues.row.pmCollected !== true ? (
              <Icon name="sync_problem" size={18} className="text-destructive" />
            ) : (
              <Icon name="check_circle_outline" size={18} className="text-success" />
            ))}
          </span>
          <span style={{ color: "var(--foreground)" }}>{isDasModel(cellValues.row.modelName) ? "" :cellValues.row.pmCollected !== true ? "Error" : "Synchronized"}</span>
        </span>
      );
    },
  },
  {
    field: "clusterId",
    headerName: "Cluster ID",
    flex: 1,
    minWidth: 100,
    renderCell: (cellValues: any) => {
      return cellValues.row.clusterId ? capitalizeWords(cellValues.row.clusterId) : "";
    },
  },
  {
    field: "actions",
    headerName: "",
    width: 90,
    minWidth: 90,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    align: "center",
    headerAlign: "center",
    renderCell: (cellValues: any) => <NodeGridActionsCell row={cellValues.row} />,
  },
];

// Helper component for Config mismatch cell
function GoldenConfigResultCell(cellValues: any) {
  const value = Number(cellValues.row.goldenAuditFailCount || 0);
  const deviceKey = cellValues.row.objectKey;
  const deviceLabel = cellValues.row.modelName === "migration" ? cellValues.row.objectKey : cellValues.row.name;
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const handleOpenDrawer = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setDrawerOpen(true);
  }, []);

  return (
    <>
      <TooltipProvider>
        <CustomTooltip>
          <TooltipTrigger asChild>
            <div className="flex" onClick={handleOpenDrawer}>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  color: value > 0 ? "var(--warning)" : "var(--foreground)",
                }}
              >
                {value > 0 && <DifferenceIcon sx={{ fontSize: "18px" }} />}
              </span>
              <span
                className="ml-2 hover:underline"
              >
                {formatCounts(value)}
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{"View configuration mismatch" + (value === 1 ? "" : "es")}</p>
          </TooltipContent>
        </CustomTooltip>
      </TooltipProvider>
      {drawerOpen && (
        <ViolationsDrawerWrapper
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          deviceKey={deviceKey}
          deviceLabel={deviceLabel}
        />
      )}
    </>
  );
}

export const violationsDrawerColumns: GridColDef[] = [
  {
    field: "datamodelKey",
    headerName: "Parameter",
    width: 700,
    renderCell: (cellValues: any) => {
      const value = cellValues.value ?? "";
      const prefix = "InternetGatewayDevice.";
      return <span className="text-xs font-mono text-muted-foreground break-all leading-relaxed">{value.startsWith(prefix) ? value.slice(prefix.length) : value}</span>;
    },
  },
  {
    field: "expectedValue",
    headerName: "Gold Standard",
    width: 130,
    renderCell: (cellValues: any) => convertSymbols(cellValues.value) != null ? <Badge variant="outline" className="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 px-1.5 py-0.5 rounded text-sm hover:bg-green-200 dark:hover:bg-green-900/60 font-normal border-transparent">{convertSymbols(cellValues.value)}</Badge> : null,
  },
  {
    field: "actualValue",
    headerName: "Configured",
    width: 130,
    renderCell: (cellValues: any) => convertSymbols(cellValues.value) != null ? <Badge variant="outline" className="inline-block bg-muted px-1.5 py-0.5 rounded text-sm text-muted-foreground dark:text-slate-300 border-transparent">{convertSymbols(cellValues.value)}</Badge> : null,
  },
];

export const eventsColumns: GridColDef[] = [
  {
    field: "geoRegionName",
    headerName: "Region",
    flex: 1,
    minWidth: 120,
    renderCell: (cellValues: any) => <span className="text-sm text-muted-foreground">{cellValues.value}</span>,
  },
  {
    field: "severity",
    headerName: "Severity",
    flex: 1,
    minWidth: 100,
    renderCell: (cellValues: any) => <StatusIcon status={cellValues.value} />,
  },
  {
    field: "occuredDateAndTimeMs",
    headerName: "Time Occured On Device",
    flex: 1,
    minWidth: 160,
    renderCell: (cellValues: any) => convertEpochToGMT(cellValues?.value, DATEFORMATS.TS),
    sortable: true,
  },
  { field: "retention", headerName: "Time Updated", flex: 1, minWidth: 140, renderCell: (cellValues: any) => convertEpochToGMT(cellValues?.value, DATEFORMATS.TS) },
  {
    field: "hostName",
    headerName: "Source",
    flex: 1,
    minWidth: 120,
    renderCell: (cellValues: any) => {
      const value = cellValues.value || "";
      return (
        <span className="group/devlink inline-flex items-center gap-1 min-w-0 text-link cursor-pointer">
          <span className="font-medium truncate group-hover/devlink:underline">{value}</span>
          <Icon name="open_in_new" size={14} className="shrink-0 opacity-0 group-hover/devlink:opacity-70 transition-opacity text-muted-foreground" />
        </span>
      );
    },
  },
  { field: "managedObject", headerName: "Managed Object", flex: 1, minWidth: 120 },
  {
    field: "displayName",
    headerName: "Type",
    flex: 1,
    minWidth: 100,
    renderCell: (cellValues: any) => {
      const valueToDisplay = cellValues.row?.displayName ?? cellValues.row?.eventType;
      return valueToDisplay || "";
    },
  },
  { field: "description", headerName: "Description", flex: 2, minWidth: 160 },
];

export const alarmColumns: GridColDef[] = [
  {
    field: "geoRegionName",
    headerName: "Region",
    flex: 1,
    minWidth: 120,
    renderCell: (cellValues: any) => <span className="text-sm text-muted-foreground">{cellValues.value}</span>,
  },
  {
    field: "severity",
    headerName: "Severity",
    flex: 1,
    minWidth: 100,
    renderCell: (cellValues: any) => <StatusIcon status={cellValues.value} />,
  },
  {
    field: "occuredDateAndTimeMs",
    headerName: "Time Occured On Device",
    flex: 1,
    minWidth: 160,
    renderCell: (cellValues: any) => convertEpochToGMT(cellValues?.value, DATEFORMATS.TS),
  },
  { field: "retention", headerName: "Time Updated", flex: 1, minWidth: 140, renderCell: (cellValues: any) => convertEpochToGMT(cellValues?.value, DATEFORMATS.TS) },
  {
    field: "hostName",
    headerName: "Source",
    flex: 1,
    minWidth: 120,
    renderCell: (cellValues: any) => {
      const value = cellValues.value || "";
      return (
        <span className="group/devlink inline-flex items-center gap-1 min-w-0 text-link cursor-pointer">
          <span className="font-medium truncate group-hover/devlink:underline">{value}</span>
          <Icon name="open_in_new" size={14} className="shrink-0 opacity-0 group-hover/devlink:opacity-70 transition-opacity text-muted-foreground" />
        </span>
      );
    },
  },
  { field: "managedObject", headerName: "Managed Object", flex: 1, minWidth: 120 },
  {
    field: "displayName",
    headerName: "Type",
    flex: 1,
    minWidth: 100,
    renderCell: (cellValues: any) => {
      const valueToDisplay = cellValues.row?.displayName ?? cellValues.row?.eventType;
      return valueToDisplay || "";
    },
  },
  { field: "description", headerName: "Description", flex: 2, minWidth: 160 },
  { field: "tickedtId", headerName: "Ticket ID", flex: 1, minWidth: 100 },
  { field: "ticketAssignedTo", headerName: "Owner", flex: 1, minWidth: 100 },
];

export const conditionsColumns: GridColDef[] = [
  {
    field: "geoRegionName",
    headerName: "Region",
    flex: 1,
    minWidth: 120,
    renderCell: (cellValues: any) => <span className="text-sm text-muted-foreground">{cellValues.value}</span>,
  },
  { field: "objectKey", headerName: "Sequence ID", flex: 1, minWidth: 100 },
  {
    field: "occuredDateAndTimeMs",
    headerName: "Time Occured On Device",
    flex: 1,
    minWidth: 140,
    renderCell: (cellValues: any) => convertEpochToGMT(cellValues?.value, DATEFORMATS.TS),
  },
  { field: "retention", headerName: "Time Updated", flex: 1, minWidth: 120, renderCell: (cellValues: any) => convertEpochToGMT(cellValues?.value, DATEFORMATS.TS) },
  {
    field: "severity",
    headerName: "Severity",
    flex: 1,
    minWidth: 100,
    renderCell: (cellValues: any) => <StatusIcon status={cellValues.value} />,
  },
  { field: "hostName", headerName: "Source", flex: 1, minWidth: 100 },
  { field: "managedObject", headerName: "Managed Object", flex: 1, minWidth: 100 },
  {
    field: "displayName",
    headerName: "Type",
    flex: 1,
    minWidth: 100,
    renderCell: (cellValues: any) => {
      const valueToDisplay = cellValues.row?.displayName ?? cellValues.row?.eventType;
      return valueToDisplay || "";
    },
  },
  { field: "description", headerName: "Description", flex: 2, minWidth: 120 },
  //{ field: "alarmState", headerName: "AlarmState", width: 100, sortable: false } /* TODO: This field does not exist in the data object */,
];

export const alarmForwardingColumns: GridColDef[] = [
  {
    field: "name", headerName: "Name", flex: 2,
    renderCell: (cellValues: any) => sanitizeHtmlInput(cellValues.row?.name),
    sortable: false
  },
  { field: "ipAddressesList", headerName: "Host Name", flex: 3, sortable: false, cellClassName: "font-mono text-xs" },
];

export const emailGroupsColumns: GridColDef[] = [
  {
    field: "name", headerName: "Name", flex: 2,
    renderCell: (cellValues: any) => sanitizeHtmlInput(cellValues.row?.name),
    sortable: false
  },
  {
    field: "emailAddresses", headerName: "Email Addresses", flex: 4, sortable: false,
    renderCell: (cellValues: any) => {
      const emails = cellValues.value;
      if (!emails) return null;
      const emailList = typeof emails === "string" ? emails.split(",").map((e: string) => e.trim()).filter(Boolean) : [];
      return emailList.length > 0 ? (
        <div className="flex items-center gap-1 flex-wrap py-1">
          {emailList.map((email: string) => (
            <Badge key={email} variant="outline" className="bg-muted px-1.5 py-0.5 rounded text-xs text-foreground dark:text-slate-300 border-transparent font-normal font-mono">{email}</Badge>
          ))}
        </div>
      ) : null;
    },
  },
];

export const tasksColumns: GridColDef[] = [
  { field: "taskName", headerName: "Task Name", width: 150 },
  { field: "taskType", headerName: "Type", width: 150 },
  { field: "startTime", headerName: "Start Time", width: 200 },
  { field: "endTime", headerName: "End Time", width: 200 },
  { field: "method", headerName: "Method", width: 150 },
  {
    field: "status",
    headerName: "Status",
    width: 150,
    renderCell: (cellValues: any) => <Status status={cellValues.row.status} />,
  },
  {
    field: "   ",
    headerName: "     ",
    width: 150,
    renderCell: () => <Options />,
  },
];

export const scheduledTasksColumns: GridColDef[] = [
  {
    field: "geoRegion",
    headerName: "Region",
    minWidth: 180,
    flex: 1,
    renderCell: (cellValues: any) => {
      return <span className="text-sm text-muted-foreground">{cellValues.value}</span>;
    }
  },
  {
    field: "name",
    headerName: "Task",
    width: 200,
    renderCell: (cellValues: any) => {
      const value = cellValues.row.name;
      return (
        <span className="group/devlink inline-flex items-center gap-1 min-w-0 text-link cursor-pointer">
          <span className="font-medium truncate group-hover/devlink:underline">{value}</span>
          <Icon
            name="open_in_new"
            size={14}
            className="shrink-0 opacity-0 group-hover/devlink:opacity-70 transition-opacity text-muted-foreground"
          />
        </span>
      );
    },
  },
  { field: "taskName", headerName: "Type", width: 200 },
  {
    field: "nextTime",
    headerName: "Next Execution Time",
    width: 200,
    renderCell: (cellValues: any) => (cellValues?.value <= 0 ? "None" : dayjs(cellValues?.value).format(DATEFORMATS.TS)),
  },
  {
    field: "lastCompleted",
    headerName: "Last Completed",
    width: 180,
    renderCell: (cellValues: any) => (cellValues?.value <= 0 ? "None" : dayjs.utc(cellValues?.value).local().format(DATEFORMATS.TS)),
  },
  { field: "period", headerName: "Frequency", width: 170 },
  {
    field: "status",
    headerName: "Status",
    width: 160,
    renderCell: (cellValues: any) => <StatusIcon status={capitalizeWords(cellValues.value)} />,
  },
  { field: "createdBy", headerName: "Created By", width: 150 },
  { field: "description", headerName: "Description", width: 340 },
];

export const templatesColumns: GridColDef[] = [
  {
    field: "templateName",
    headerName: "Template Name",
    flex: 4,
    sortable: false,
    renderCell: (cellValues: any) => convertSymbols(cellValues.value),
  },
  {
    field: "goldStandard",
    headerName: "",
    flex: 1,
    sortable: false,
    align: "center",
    headerAlign: "center",
    renderCell: (cellValues: any) =>
      cellValues?.row?.goldStandard ? (
        <Tooltip title="Used in Gold Standard">
          <StarIcon fontSize="small" sx={{ color: "#d4a017" }} />
        </Tooltip>
      ) : (
        <Box />
      ),
  },
  {
    field: "imageConstraints",
    headerName: "Image Constraints",
    flex: 4,
    sortable: false,
    renderCell: (cellValues: any) => convertSymbols(cellValues.value),
  },
  {
    field: "description",
    headerName: "Description",
    width: 220,
    sortable: false,
    renderCell: (cellValues: any) => convertSymbols(cellValues.value),
  },
  { field: "nodeTypeKey", headerName: "Device Type ", flex: 3, sortable: false },
  {
    field: "actions",
    type: "actions",
    flex: 1,
    renderCell: (cellValues: any) => (
      <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
        <ExportTemplate selectedRow={cellValues?.row} />
        <RemoveTemplate selectedRow={cellValues?.row} />
      </div>
    ),
  },
];

export const localtemplatesColumns = (Type: string) => {
  let arr = [
    { field: "templateName", headerName: "Task", width: 300, renderCell: (cellValues: any) => convertSymbols(cellValues.value) },
    { field: "description", headerName: "Description", width: 300, renderCell: (cellValues: any) => convertSymbols(cellValues.value) },
    { field: "imageConstraints", headerName: "Image Constraints", width: 200, renderCell: (cellValues: any) => convertSymbols(cellValues.value) },
    { field: "nodeTypeKey", headerName: "Node Type ", width: 100 },
    {
      field: "   ",
      headerName: "     ",
      width: 100,
      renderCell: (cellValues: any) =>
        Type === "localTab" ? (
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
            <ExportTemplate selectedRow={cellValues?.row} />
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
            <FileSelect selectedRow={cellValues?.row} />
          </div>
        ),
    },
  ];
  return arr;
};

export const panelTemplatesColumns: GridColDef[] = [
  { field: "templateName", headerName: "Template Name", flex: 3, minWidth: 200, renderCell: (cellValues: any) => convertSymbols(cellValues.value) },
  { field: "description", headerName: "Description", width: 300, renderCell: (cellValues: any) => convertSymbols(cellValues.value) },
  { field: "imageConstraints", headerName: "Image Constraints", flex: 2, minWidth: 150, renderCell: (cellValues: any) => convertSymbols(cellValues.value) },
  { field: "nodeTypeKey", headerName: "Node Type ", flex: 1, minWidth: 100 },
  {
    field: "   ",
    headerName: "     ",
    width: 100,
    renderCell: (cellValues: any) => (
      <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
        <ExportTemplate selectedRow={cellValues?.row} />
        <RemoveTemplate selectedRow={cellValues?.row} type={"panel"} />
      </div>
    ),
  },
];

export const configTemplateColumns: GridColDef[] = [
  { field: "templateName", headerName: "Task", flex: 1, renderCell: (cellValues: any) => convertSymbols(cellValues.value) },
  { field: "description", headerName: "Description", flex: 1, renderCell: (cellValues: any) => convertSymbols(cellValues.value) },
  { field: "imageConstraints", headerName: "Image Constraints", flex: 1, renderCell: (cellValues: any) => convertSymbols(cellValues.value) },
  { field: "nodeTypeKey", headerName: "Node Type", flex: 1 },
];

export const taskLogsColumns: GridColDef[] = [
  {
    field: "status",
    headerName: "Status",
    width: 120,
    renderCell: (cellValues: any) => <StatusIcon status={cellValues.value} />,
    sortable: false,
  },
  {
    field: "startDate",
    headerName: "Start Time",
    width: 160,
    renderCell: (cellValues: any) => (cellValues?.value <= 0 ? "" : dayjs(cellValues?.value).format(DATEFORMATS.TS)),
    sortable: false,
  },
  {
    field: "endDate",
    headerName: "End Time",
    width: 160,
    renderCell: (cellValues: any) => (cellValues?.value <= 0 ? "" : dayjs(cellValues?.value).format(DATEFORMATS.TS)),
    sortable: false,
  },
  {
    field: "initiatedBy",
    headerName: "Initiated By",
    width: 130,
    sortable: false,
  },
  {
    field: "total",
    headerName: "Total",
    width: 90,
    align: "center",
    headerAlign: "center",
    sortable: false,
  },
  {
    field: "success",
    headerName: "Success",
    width: 90,
    align: "center",
    headerAlign: "center",
    sortable: false,
  },
  {
    field: "failed",
    headerName: "Failed",
    width: 90,
    align: "center",
    headerAlign: "center",
    sortable: false,
  },
  {
    field: "idle",
    headerName: "Idle",
    width: 90,
    align: "center",
    headerAlign: "center",
    sortable: false,
  },
  {
    field: "running",
    headerName: "Running",
    width: 90,
    align: "center",
    headerAlign: "center",
    sortable: false,
  },
];

export const allTaskLogsColumns: GridColDef[] = [
  {
    field: "name",
    headerName: "Task",
    width: 200,
  },
  {
    field: "status",
    headerName: "Status",
    width: 150,
    renderCell: (cellValues: any) => <StatusIcon status={cellValues.value} />,
  },
  {
    field: "startTime",
    headerName: "Start Time",
    width: 150,
    renderCell: (cellValues: any) => (cellValues?.value <= 0 ? "" : dayjs(cellValues?.value).format(DATEFORMATS.TS)),
  },
  {
    field: "endTime",
    headerName: "End Time",
    width: 150,
    renderCell: (cellValues: any) => (cellValues?.value <= 0 ? "" : dayjs(cellValues?.value).format(DATEFORMATS.TS)),
  },
  {
    field: "initiatedBy",
    headerName: "Initiated By",
    width: 150,
  },
  // {
  //   field: "   ",
  //   headerName: "     ",
  //   width: 100,
  //   renderCell: (cellValues: any) => (
  //       <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
  //         <ExportTemplate selectedRow={cellValues?.row} />
  //         <DeleteOutline />
  //         {/* <Options />  */}
  //       </div>
  //   )
  // },
];

export const allTemplateLogsColumns: GridColDef[] = [
  {
    field: "name",
    headerName: "Template Name",
    flex: 1,
    minWidth: 200,
    sortable: false,
    renderCell: (cellValues: any) => convertSymbols(cellValues.value),
  },
  {
    field: "status",
    headerName: "Status",
    width: 150,
    renderCell: (cellValues: any) => <StatusIcon status={cellValues.value} />,
    sortable: false,
  },
];

export const taskLogDetailsColumns: GridColDef[] = [
  {
    field: "name",
    headerName: "Name",
    width: 200,
    sortable: false,
  },
  {
    field: "status",
    headerName: "Status",
    width: 150,
    renderCell: (cellValues: any) => <StatusIcon status={cellValues.value} />,
    sortable: false,
  },
  {
    field: "percentage",
    headerName: "Progress",
    width: 200,
    renderCell: (cellValues: any) => <ProgressBar value={cellValues.value} />,
    sortable: false,
  },
  {
    field: "details",
    headerName: "Details",
    minWidth: 650,
    sortable: false,
  },
];

export const ipInterfaceColumns: GridColDef[] = [
  { field: "index", headerName: "LAN Device ID", flex: 1, minWidth: 80 },
  { field: "description", headerName: "Description", flex: 2, minWidth: 120 },
  { field: "ipInterfaceIpAddress", headerName: "IP Address", flex: 1, minWidth: 120 },
  { field: "vlanId", headerName: "VLAN", flex: 1, minWidth: 80 },
  { field: "ethernetId", headerName: "Ethernet ID", flex: 2, minWidth: 140 },
  {
    field: "operState",
    headerName: "Status",
    flex: 1,
    minWidth: 100,
    renderCell: (cellValues: any) => <StatusIcon status={cellValues.row.operState} />,
  },
  {
    field: "alarms",
    headerName: "Alarms",
    flex: 1,
    minWidth: 100,
    renderCell: (cellValues: any) => <StatusIcon status={cellValues.row.alarms} />,
  },
  {
    field: "",
    headerName: "",
    renderCell: () => <Options />,
  },
];

export const radioNodesColumns: GridColDef[] = [
  { field: "id", headerName: "Index", minWidth: 80, flex: 1, sortable: false },
  { field: "name", headerName: "Name", flex: 2, minWidth: 120, sortable: false },
  { field: "description", headerName: "Description", flex: 2, minWidth: 120, sortable: false },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    minWidth: 100,
    sortable: false,
    renderCell: (cellValues: any) => <StatusIcon status={cellValues.row.status} />,
  },
  {
    field: "enable",
    headerName: "Enabled",
    flex: 1,
    minWidth: 100,
    renderCell: (cellValues: any) => <StatusIcon status={cellValues.row.enable ? "Enabled" : "Disabled"} isLabel={false} />,
    sortable: false,
  },
  { field: "ethernetId", headerName: "Ethernet ID", flex: 2, minWidth: 140, sortable: false },
  { field: "outerIpAddress", headerName: "IP Address", flex: 1, minWidth: 120, sortable: false },
  {
    field: "model",
    headerName: "Model #",
    flex: 1,
    minWidth: 100,
    sortable: false,
    renderCell: (cellValues: any) =>
      cellValues.row.model ? (
        cellValues.row.model
      ) : statusRNColumns?.some((status) => status === cellValues.row.status) ? (
        <StatusIcon status={"Syncing"} />
      ) : (
        ""
      ),
  },
];

export const nrCellsColumns = (radioNodes: IRadioNodesDataGridRow[]) => {
  let arr = [
    { field: "id", headerName: "Cell ID", minWidth: 100, flex: 1, sortable: false },
    { field: "name", headerName: "Name", width: 150, sortable: false },
    { field: "description", headerName: "Description", width: 150, sortable: false },
    {
      field: "status",
      headerName: "Status",
      width: 180,
      renderCell: (cellValues: any) => <StatusIcon status={cellValues.row.status} />,
      sortable: false,
    },
    {
      field: "enable",
      headerName: "Enabled",
      width: 100,
      renderCell: (cellValues: any) => <StatusIcon status={cellValues.row.enable ? "Enabled" : "Disabled"} isLabel={false} />,
      sortable: false,
    },
    // {
    //   field: "alarms",
    //   headerName: "Alarms",
    //   width: 200,
    //   renderCell: (cellValues: any) => <StatusIcon status={cellValues.row.alarms} />,
    // },
    { field: "zoneId", headerName: "Zone ID", width: 150, sortable: false },
    {
      field: "radioNode",
      headerName: "Radio Node",
      width: 180,
      sortable: false,
      renderCell: (cellValues: any) => (radioNodes?.length > 0 ? renderCellRadioNode(radioNodes, cellValues?.row?.radioNode) : ""),
    },
    { field: "dlBandwidth", headerName: "DL Bandwidth(#RB)", width: 200, sortable: false },
    // {
    //   field: "actions",
    //   type: "actions",
    //   renderCell: () => <Options />,
    // },
  ];
  return arr;
};

export const lteZonesColumns = (nodeType: string) => {
  let arr = [
    { field: "index", headerName: "ID", flex: 1, sortable: false },
    { field: "description", headerName: "Description", flex: 1, sortable: false },
    // { field: "nrcell", headerName: "Cell", width: 150, sortable: false },
    {
      field: "barringforemergency",
      headerName: "911 Enabled",
      flex: 1,
      renderCell: (cellValues: any) => (
        <StatusIcon status={cellValues.row.barringforemergency === "True" ? "Enabled" : "Disabled"} isLabel={false} />
      ),
      sortable: false,
    },
    { field: "eARFCNUL", headerName: "ARFCNUL", flex: 1, sortable: false },
    { field: "ulBandwidth", headerName: "ULBandwidth(#RB)", flex: 1, sortable: false },
    { field: "eARFCNDL", headerName: "ARFCNDL", flex: 1, sortable: false },
    { field: "dlBandwidth", headerName: "DLBandwidth(#RB)", flex: 1, sortable: false },
  ];
  if (nodeType !== "SN") {
    arr.splice(2, 1);
  }
  return arr;
};

export const renderCellRadioNode = (radioNodes: IRadioNodesDataGridRow[], val: string) => {
  if (radioNodes?.length > 0 && val) {
    const filteredData = radioNodes?.filter((el) => Number(el?.id) === Number(val));
    if (filteredData?.length > 0) {
      if (filteredData[0]?.model) {
        return <Link href="#">{val}</Link>;
      } else {
        return (
          <div>
            <span>{val}</span>
            &nbsp;
            <StatusIcon status={"Syncing"} />
          </div>
        );
      }
    } else return "";
  } else return "";
};

export const lteCellsColumns = (radioNodes: IRadioNodesDataGridRow[]) => {
  let arr = [
    { field: "id", headerName: "Cell ID", minWidth: 80, flex: 1 },
    { field: "name", headerName: "Name", flex: 2, minWidth: 120 },
    { field: "description", headerName: "Description", flex: 2, minWidth: 120 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderCell: (cellValues: any) => <StatusIcon status={cellValues.row.status} />,
    },
    {
      field: "enable",
      headerName: "Enabled",
      flex: 1,
      minWidth: 100,
      renderCell: (cellValues: any) => <StatusIcon status={cellValues.row.enable ? "Enabled" : "Disabled"} isLabel={false} />,
      sortable: false,
    },
    { field: "zoneId", headerName: "Zone ID", flex: 1, minWidth: 100, sortable: false },
    {
      field: "radioNode",
      headerName: "Radio Node",
      flex: 2,
      minWidth: 120,
      sortable: false,
      renderCell: (cellValues: any) => (radioNodes?.length > 0 ? renderCellRadioNode(radioNodes, cellValues?.row?.radioNode) : ""),
    },
    { field: "dlBandwidth", headerName: "DL Bandwidth(#RB)", flex: 1, minWidth: 120, sortable: false },
  ];
  return arr;
};

export const lteX2connectionsColumns: GridColDef[] = [
  { field: "index", headerName: "Connection ID", minWidth: 100, flex: 1, sortable: false },
  { field: "name", headerName: "X2 Peer Name", flex: 2, minWidth: 120, sortable: false },
  {
    field: "enable",
    headerName: "Peer Enabled",
    flex: 1,
    minWidth: 100,
    renderCell: (cellValues: any) => <Checkbox checked={cellValues.row.enable} disabled className="m-0" />,
    sortable: false,
  },
  {
    field: "isENDCX2",
    headerName: "IsENDCX2",
    flex: 1,
    minWidth: 80,
    renderCell: (cellValues: any) => <span>{cellValues.row.isENDCX2 ? "Yes" : "No"}</span>,
    sortable: false,
  },
  {
    field: "connectionState",
    headerName: "Status",
    flex: 1,
    minWidth: 100,
    renderCell: (cellValues: any) => <StatusIcon status={cellValues.row.connectionState} tooltip={cellValues.row.connectionState} />,
    sortable: false,
  },
  { field: "eNodeBID", headerName: "ENodeB ID", flex: 1, minWidth: 100, sortable: false },
  { field: "localAddress", headerName: "Local Address", flex: 1, minWidth: 100, sortable: false },
  { field: "remoteAddress", headerName: "Remote Address", flex: 1, minWidth: 100, sortable: false },
  { field: "plmnId", headerName: "PLMN ID", flex: 1, minWidth: 100, sortable: false },
  { field: "port", headerName: "Port", flex: 1, minWidth: 80, sortable: false },
  { field: "type", headerName: "Type", flex: 1, minWidth: 80, sortable: false },
];

export const ngX2connectionsColumns: GridColDef[] = [
  { field: "index", headerName: "Connection ID", minWidth: 120, flex: 1, sortable: false },
  { field: "name", headerName: "X2 Peer Name", width: 150, sortable: false },
  {
    field: "enable",
    headerName: "Peer Enabled",
    width: 150,
    // renderCell: (cellValues: any) => (
    //   <CheckboxNoLabel value={handleBooleanStringValues(cellValues.row.enable)} rowId={cellValues.row.id} disabled={true} />)
    renderCell: (cellValues: any) => <Checkbox checked={cellValues.row.enable} disabled className="m-0" />,
    sortable: false,
  },
  {
    field: "connectionState",
    headerName: "Status",
    width: 150,
    renderCell: (cellValues: any) => <StatusIcon status={cellValues.row.connectionState} tooltip={cellValues.row.connectionState} />,
    sortable: false,
  },
  // {
  //   field: "alarms",
  //   headerName: "Alarms",
  //   width: 150,
  //   renderCell: (cellValues: any) => <StatusIcon status={cellValues.row.alarms} />,
  // },
  { field: "eNodeBID", headerName: "ENodeB ID", width: 150, sortable: false },
  { field: "localAddress", headerName: "Local Address", width: 150, sortable: false },
  { field: "remoteAddress", headerName: "Remote Address", width: 150, sortable: false },
  { field: "plmnId", headerName: "PLMN ID", width: 150, sortable: false },
];

export const getEventsConfigurationColumns = (
  handleEdit: (row: IEventConfig) => void,
  handleDelete: (row: IEventConfig) => void
): GridColDef[] => [
    { field: "displayName", headerName: "Event", flex: 3, minWidth: 180 },
    { field: "category", headerName: "Category", flex: 1.5, minWidth: 120 },
    {
      field: "severity",
      headerName: "Severity",
      flex: 1.5,
      minWidth: 120,
      renderCell: (cellValues: any) => {
        const severity = cellValues.row.severity;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <StatusIcon isLabel={false} status={severity} />
            <span>{severity}</span>
          </Box>
        );
      },
    },
    {
      field: "snmpManagerGroup",
      headerName: "Forward To SNMP",
      flex: 1.5,
      minWidth: 140,
      renderCell: (cellValues: any) => (
        <span>{cellValues.row.snmpManagerGroup || "None"}</span>
      ),
    },
    {
      field: "emailGroup",
      headerName: "Forward To Email",
      flex: 1.5,
      minWidth: 140,
      renderCell: (cellValues: any) => (
        <span>{cellValues.row.emailGroup || "None"}</span>
      ),
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      filterable: false,
      width: 50,
      renderCell: (cellValues: any) => {
        const row = cellValues.row as IEventConfig;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 border-0 bg-transparent rounded-full text-foreground hover:bg-accent"
                aria-label="Event actions"
              >
                <MoreVertical className="h-6 w-6 font-bold" strokeWidth={3} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => { e.preventDefault(); handleEdit(row); }}
                onSelect={(e) => e.preventDefault()}
              >
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={true}
                onClick={() => handleDelete(row)}
                className="text-red-500"
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

export const goldenConfigurationColumns: GridColDef[] = [
  {
    field: "nodeType",
    headerName: "Node Type",
    width: 150,
    flex: 2
  },
  {
    field: "description",
    headerName: "Description",
    flex: 3,
    editable: true,
    renderCell: (cellValues: any) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: "100%",
          "& .editable-icon": { visibility: "hidden" },
          "&:hover .editable-icon": { visibility: "visible" },
        }}
      >
        <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {convertSymbols(cellValues.value)}
        </Box>
        <EditIcon className="editable-icon" sx={{ fontSize: 16, color: "#64748b" }} />
      </Box>
    ),
  },
  { field: "technology", headerName: "Technology", flex: 1 },
  {
    field: "version",
    headerName: "Image Constraints",
    flex: 2,
    renderCell: (cellValues: any) => convertSymbols(cellValues.value),
  },
  {
    field: "lastUpdated",
    headerName: "Last Updated",
    flex: 2,
    renderCell: (cellValues: any) => (cellValues?.value ? convertEpochToGMT(cellValues.value, DATEFORMATS.TS) : "-"),
  },
  {
    field: "lastRun",
    headerName: "Last Run",
    flex: 2,
    renderCell: (cellValues: any) => (cellValues?.value ? convertEpochToGMT(cellValues.value, DATEFORMATS.TS) : "-"),
  },
  {
    field: "result",
    headerName: "Results",
    flex: 2,
    sortable: false,
    renderCell: (cellValues: any) =>
      cellValues?.row?.scheduleEnabled === false ? (
        <Typography variant="body2">-</Typography>
      ) : (<>
        <div className="flex items-center gap-3">
          <TooltipProvider delayDuration={200}>
            <CustomTooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1 text-success cursor-pointer" data-state="closed">
                  <span className="material-icons select-none" aria-hidden="true" style={{ fontSize: 16 }}>check_circle</span>
                  <span className="tabular-nums text-sm">{cellValues?.row?.passCount ?? 0}</span>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" align="center" sideOffset={4} className="z-[1400]">
                {`${cellValues?.row?.passCount ?? 0} passed`}
              </TooltipContent>
            </CustomTooltip>
          </TooltipProvider>

          <TooltipProvider delayDuration={200}>
            <CustomTooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1 text-destructive cursor-pointer" data-state="closed">
                  <span className="material-icons select-none" aria-hidden="true" style={{ fontSize: 16 }}>cancel</span>
                  <span className="tabular-nums text-sm">{cellValues?.row?.failCount ?? 0}</span>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" align="center" sideOffset={4} className="z-[1400]">
                {`${cellValues?.row?.failCount ?? 0} failed`}
              </TooltipContent>
            </CustomTooltip>
          </TooltipProvider>

          <TooltipProvider delayDuration={200}>
            <CustomTooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1 text-muted-foreground cursor-pointer" data-state="closed">
                  <span className="material-icons select-none" aria-hidden="true" style={{ fontSize: 16 }}>skip_next</span>
                  <span className="tabular-nums text-sm">{cellValues?.row?.skipCount ?? 0}</span>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" align="center" sideOffset={4} className="z-[1400]">
                {`${cellValues?.row?.skipCount ?? 0} skipped`}
              </TooltipContent>
            </CustomTooltip>
          </TooltipProvider>
        </div>
      </>
      ),
  },
  {
    field: "actions",
    headerName: "",
    width: 80,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params: any) => (
      <div onClick={(event) => event.stopPropagation()}>
        <GoldenDefinitionActionsMenu
          id={params.row.id}
          name={params.row.definitionName}
          scheduleEnabled={params.row.scheduleEnabled}
        />
      </div>
    ),
  },
];

export const auditTrailColumns: GridColDef[] = [
  { field: "id", headerName: "Seq No.", flex: 1 },
  {
    field: "date",
    headerName: "Operation Time & Date",
    flex: 2,
    // renderCell: (cellValues: any) => dayjs.utc(cellValues.row.date).local().format(),
    renderCell: (cellValues: any) => (cellValues.row.date <= 0 ? "" : dayjs(cellValues.row.date).format(DATEFORMATS.TS)),
  },
  { field: "userId", headerName: "User Name", flex: 1 },
  { field: "action", headerName: "Operation Name", flex: 3 },
  // { field: "userAddress", headerName: "Client IP Address", width: 200 },
  {
    field: "allowed",
    headerName: "Operation Status",
    flex: 1,
    renderCell: (cellValues: any) => {
      const isAllowed = Boolean(cellValues.row.allowed);

      return (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 70,
            px: 1,
            py: 0.5,
            borderRadius: "0.5rem",
            fontSize: "0.8rem",
            fontWeight: "0.8rem",
            backgroundColor: isAllowed ? "var(--secondary)" : "#DC2626",
            color: isAllowed
              ? 'var(--secondary-foreground)'
              : '#ffffff',
          }}
        >
          {isAllowed ? "Allowed" : "Denied"}
        </Box>
      );
    },
  },
];

export const thresholdCrossingSettingActionColumns = ({ rowCount = 0, canManage = true }: { rowCount?: number; canManage?: boolean }): GridColDef[] => {
  const columns: GridColDef[] = [];
  columns.push(
    { field: "actionType", headerName: "Action", flex: 1, sortable: false, },
    {
      field: "details",
      headerName: "Details",
      flex: 1,
      sortable: false,
      renderCell: (cellValues: any) => {
        const values = Array.isArray(cellValues.value) ? cellValues.value : [cellValues.value];
        return (
          <div className="flex flex-wrap gap-1">
            {values.map((val: any, index: number) => (
              <Badge key={index} variant="secondary" className="font-semibold">
                {val}
              </Badge>
            ))}
          </div>
        );
      }
    });
  if (canManage) {
    columns.push(
      {
        field: "actions", type: "actions", sortable: false,
        renderCell: (cellValues: any) => <ProfileActionMenu tableRow={cellValues} disableDelete={rowCount <= 1} />
      }
    )
  }
  return columns;
}


export const thresholdCrossingSettingRuleColumns = ({ rowCount = 0, canManage = true }: { rowCount?: number; canManage?: boolean }): GridColDef[] => {
  const columns: GridColDef[] = [];
  columns.push(
    { field: "kpi", headerName: "KPI", flex: 2, sortable: false, },
    {
      field: "kpiType", headerName: "KPI Type", flex: 1, sortable: false,
      renderCell: (cellValues: any) => {
        const kpiType = <span className="p-2 align-middle px-4 text-muted-foreground">{cellValues.value}</span>;
        return kpiType;
      },
    },
    {
      field: "thresholdValue",
      headerName: "Alert When..",
      width: 170,
      sortable: false,
      renderCell: (cellValues: any) => {
        const row = cellValues.row;
        const above = row.thresholdAbove;
        const below = row.thresholdBelow;
        let text = " ";
        if (above == null) {
          text = "< " + below;
        } else if (below == null) {
          text = "> " + above;
        }
        return <span className="p-2 align-middle px-4 text-muted-foreground">{text}</span>;
      }
    },
    {
      field: "consecutiveSamples",
      headerName: "Consecutive Samples",
      flex: 1,
      sortable: false,
      renderCell: (cellValues: any) => {
        return <span className="p-2 align-middle px-4 text-muted-foreground">{cellValues.value}</span>;
      }
    },
  )
  if (canManage) {
    columns.push(
      { field: "actions", type: "actions", sortable: false, renderCell: (cellValues: any) => <ProfileRuleActionMenu tableRow={cellValues} disableDelete={rowCount <= 1} /> }
    );
  }
  return columns;
}
export const lanDeviceInterfaceColumn: IColumnWithChild = {
  parentColumn: [
    // { field: "actions", type: "actions", headerName: "Action" },
    {
      field: "name",
      headerName: "Name",
    },
    {
      field: "enable",
      headerName: "Enable",
      renderCell: (cellValues: any) => cellValues?.enable && <Checkbox checked={cellValues.enable === "true"} disabled className="m-0" />,
    },
    { field: "deviceId", headerName: "Device ID" },
    {
      field: "status",
      headerName: "Status",
      renderCell: (cellValues: any) => <LANDeviceStatus tableRow={cellValues} />,
      // renderCell: (cellValues: any) => (
      //   <StatusIcon status={!!cellValues.status && statusStr.includes(cellValues.status) ? "InProgress" : cellValues.status} />
      // ),
    },
    { field: "maxBitRate", headerName: "Max Bit Rate" },
    { field: "duplex", headerName: "Duplex" },
    { field: "operState", headerName: "OperState", renderCell: (cellValues: any) => <StatusIcon status={cellValues.operState} /> },
    { field: "macAddress", headerName: "Mac Address" },
    { field: "actions", type: "actions", renderCell: (cellValues: any) => <LANDeviceActionMenu tableRow={cellValues} /> },
  ],
  childColumn: [
    { field: "index", headerName: "Index" },
    {
      field: "description",
      headerName: "Description",
    },
    { field: "operState", headerName: "Oper State", renderCell: (cellValues: any) => <StatusIcon status={cellValues.operState} /> },
    {
      field: "dhcpServerEnable",
      headerName: "DHCP Server",
      renderCell: (cellValues: any) => cellValues?.dhcpServerEnable && <Checkbox checked={cellValues.dhcpServerEnable === "true"} disabled className="m-0" />,
    },
    { field: "egressQosGroup", headerName: "Egress Qos Profile" },
    { field: "ipInterfaceIpAddress", headerName: "IP Address" },
    { field: "ipInterfaceSubnetMask", headerName: "Subnet Mask" },
    { field: "forwardingGroupIndex", headerName: "FGI", toolTipTitle: "Farwording Group Index" },
    { field: "actions", type: "actions", renderCell: (cellValues: any) => <IPInterfaceActionMenu tableRow={cellValues} /> },
  ],
  childKey: "ipInterfaces",
};

export const RfFeedsColumns: GridColDef[] = [
  { field: "index", headerName: "Index", width: 120, sortable: false },
  { field: "type", headerName: "Type", width: 150, sortable: false },
  { field: "dlarfcn", headerName: "DLARFCN", width: 150, sortable: false },
  { field: "ularfcn", headerName: "ULARFCN", width: 150, sortable: false },
  { field: "dlBandwidth", headerName: "DLBandwidth", width: 150, sortable: false },
  { field: "ulBandwidth", headerName: "ULBandwidth", width: 150, sortable: false },
  //{ field: "dlAttenuation", headerName: "DLAttenuation", width: 150, sortable: false },
  { field: "txPowerPercentage", headerName: "TxPowerPercentage", width: 150, sortable: false },
];

export const Rn620sColumns: GridColDef[] = [
  { field: "index", headerName: "Index", width: 120, sortable: false },
  { field: "name", headerName: "Name", width: 150, sortable: false },
  { field: "onlineState", headerName: "Online State", width: 150, sortable: false },
  { field: "ethernetAddress", headerName: "Ethernet Address", width: 150, sortable: false },
  { field: "serialNumber", headerName: "Serial Number", width: 150, sortable: false },
  { field: "locationDescription", headerName: "Location Description", width: 150, sortable: false },
  { field: "modelName", headerName: "Model Name", width: 150, sortable: false },
  { field: "firmwareVersion", headerName: "Firmware Version", width: 150, sortable: false },
];

export const InventoryColumns: GridColDef[] = [
  {
    field: "type",
    headerName: "Type",
    sortable: false,
    width: 200,
    renderCell: (cellValues: any) => (!cellValues.row.type ? "" : <Chip label={cellValues.row.type} sx={{ backgroundColor: "var(--muted)", color: "var(--foreground)" }} />),
  },
  { field: "index", headerName: "Index", sortable: false, width: 80 },
  { field: "deviceId", headerName: "DeviceID", sortable: false, width: 100 },
  { field: "parentPort", headerName: "Parent Port", sortable: false, width: 100 },
  { field: "name", headerName: "Name", sortable: false, width: 200 },
  { field: "onlineState", headerName: "Online State", sortable: false, width: 200 },
  {
    field: "enable",
    headerName: "Enable",
    sortable: false,
    width: 100,
    renderCell: (cellValues: any) => <Checkbox checked={cellValues.row.enable === "true"} disabled className="m-0" />,
  },
  { field: "ethernetAddress", headerName: "Ethernet Address", sortable: false, width: 350 },
  { field: "serialNumber", headerName: "Serial Number", sortable: false, width: 200 },
  { field: "location", headerName: "Location", sortable: false, width: 200 },
  { field: "modelName", headerName: "Model Name", sortable: false, width: 200 },
  { field: "firmwareVersion", headerName: "Firmware Version", sortable: false, width: 250 },
  // { field: "maxTxPowerPerAnt", headerName: "maxTxPowerPerAnt", sortable: false, width: 300 },
  // { field: "actions", type: "actions", width: 50, renderCell: (cellValues: IProTableRow) => <RFFeedActionMenu tableRow={cellValues} /> },
];

export const AlarmsInDrawerColumns: GridColDef[] = [
  {
    field: "occuredDateAndTimeMs",
    headerName: "Time Occured On Device",
    minWidth: 200,
    sortable: false,
    renderCell: (cellValues: any) => convertEpochToGMT(cellValues?.value, DATEFORMATS.TS),
  },
  // The Alarm was last updated in AMS at the "retention" timestamp, not the "receiveDateAndTime" timestamp
  {
    field: "retention",
    headerName: "Time Updated",
    minWidth: 150,
    sortable: false,
    renderCell: (cellValues: any) => convertEpochToGMT(cellValues?.value, DATEFORMATS.TS),
  },
  { field: "eventType", headerName: "Type", width: 140, sortable: false },
  {
    field: "severity",
    headerName: "Severity",
    width: 120,
    sortable: false,
    renderCell: (cellValues: any) => <StatusIcon status={cellValues.row.severity} />,
  },
];

export const setDebugLevelColumns: GridColDef[] = [
  {
    field: "module",
    headerName: "Module",
    width: 130,
    editable: true,
    sortable: false,
    type: "singleSelect",
    valueOptions: Object.keys(moduleGroups),
    preProcessEditCellProps: (params: any) => (params.props.value === "-" ? { ...params.props, error: true } : { ...params.props, error: false }),
  },
  {
    field: "level",
    headerName: "Level",
    width: 120,
    editable: true,
    sortable: false,
    type: "singleSelect",
    valueOptions: levelOptions,
    preProcessEditCellProps: (params: any) => (params.props.value === "-" ? { ...params.props, error: true } : { ...params.props, error: false }),
  },
  {
    field: "group",
    headerName: "Group",
    width: 130,
    editable: true,
    sortable: false,
    type: "singleSelect",
    valueOptions: (cellValues: any) => moduleGroups[cellValues.row.module],
    preProcessEditCellProps: (params: any) => (params.props.value === "-" ? { ...params.props, error: true } : { ...params.props, error: false }),
  },
  {
    field: "nodeId",
    headerName: "Node ID",
    width: 100,
    editable: true,
    headerAlign: "left",
    align: "left",
    sortable: false, //type:"number",
  },
  {
    field: "actions",
    type: "actions",
    width: 60,
    cellClassName: "actions",
    getActions: (row: any) => {
      return [<DeleteRowFromSetDebug id={row.id} />];
    },
  },
];

export const customEventsColumns: GridColDef[] = [
  { field: "name", headerName: "Name", flex: 1.5, sortable: true },
  { field: "description", headerName: "Description", flex: 1.5, sortable: true },
  { field: "matchString", headerName: "Pattern", flex: 1.5, sortable: true },
  {
    field: "regex", headerName: "Regex", flex: 1, sortable: true,
    renderCell: (cellValues: any) => {
      return <Badge variant="secondary">{cellValues.row.regex ? "Yes" : "No"}</Badge>
    }
  },
  {
    field: "matchCase", headerName: "Match Case", flex: 1, sortable: true,
    renderCell: (cellValues: any) => {
      return <Badge variant="secondary">{cellValues.row.matchCase ? "Yes" : "No"}</Badge>
    }
  },
  { field: "triggerType", headerName: "Trigger Type", flex: 1, sortable: true },
];

export const usersFileMgmtColumn: GridColDef[] = [
  { field: "username", headerName: "User Name", flex: 5, sortable: false },
  {
    field: "actions",
    type: "actions",
    flex: 0.5,
    renderCell: (cellValues: any) => <ActionItems rowData={cellValues.row} />,
  },
];

export const externalUserFileMgmt: GridColDef[] = [
  { field: "username", headerName: "User Name", flex: 5, sortable: false },

  {
    field: "actions",
    type: "actions",
    flex: 0.5,
    renderCell: (cellValues: any) => <ExternalActionItems rowData={cellValues.row} />,
  },
];

export const nodeFileMgmtColumn: GridColDef[] = [
  {
    field: "moduleId",
    headerName: "Module ID",
    width: 150,
    sortable: false,
    renderCell: (params: any) => (
      <Typography title={params.row.moduleId} component={"div"} sx={{ overflow: "hidden", textOverflow: "ellipsis", fontWeight: "normal" }}>
        {fileMgmtModuleList.filter((item: any) => item.value === params.row.moduleId)[0].text}
      </Typography>
    ),
  },
  { field: "hostName", headerName: "Host ", width: 130, sortable: false },
  {
    field: "generateSCOSSSHKey",
    headerName: "Generate SCOS SSH Key",
    width: 130,
    sortable: false,
    renderCell: (cellValues: any) => <Checkbox checked={cellValues.row.generateSCOSSSHKey} disabled className="m-0" />,
  },
  // {
  //   field: "autoPopulatedAuthorizedKeyFile", headerName: "Populate Authorized Keys File", width: 130, sortable: false,
  //   renderCell: (cellValues: any) => <Checkbox disabled checked={cellValues.row.autoPopulatedAuthorizedKeyFile} />,
  // },
];

export const migrationColumn: GridColDef[] = [
  { field: "name", headerName: "Name", flex: 2 },
  { field: "siteGroupName", headerName: "Group", flex: 1 },
  { field: "serialNumber", headerName: "Serial Number", flex: 1.2 },
  { field: "ipAddress", headerName: "IP Address", flex: 1 },
  { field: "snetIpAddress", headerName: "From URL", flex: 1.5 },
  {
    field: "created",
    headerName: "Date/Time",
    flex: 1.5,
    renderCell: (cellValues: any) => (cellValues?.created <= 0 ? "" : dayjs(cellValues.row?.created).format(DATEFORMATS.TS)),
  },
  {
    field: "migrationStatus",
    headerName: "Migration Status",
    flex: 1.4,
    renderCell: (cellValues: any) => {
      return !!cellValues.row.migrationStatus ? (
        <StatusIcon
          tooltip={
            cellValues.row.migrationStatus.includes("Err") ? cellValues.row.error : `${constStr.migrateStatus[cellValues.row.migrationStatus]}`
          }
          status={cellValues.row.migrationStatus.includes("Err") ? "Error" : cellValues.row.migrationStatus}
        />
      ) : (
        ""
      );
    },
  },
  {
    field: "connectionStatus",
    headerName: "Connection Status",
    flex: 1.5,
    renderCell: (cellValues: any) => {
      if (cellValues.row.connectionStatus) {
        return <StatusIcon status={cellValues.row.connectionStatus} />;
      }
      return "-";
    },
  },
];

export const siteMigrationColumn: GridColDef[] = [
  { field: "name", headerName: "Name", width: 250 },
  { field: "ipAddress", headerName: "IP Address", width: 200 },
  { field: "serialNumber", headerName: "Serial Number", width: 320 },
  {
    field: "connectionStatus",
    headerName: "Connection Status",
    width: 200,
    renderCell: (cellValues: any) => {
      return !!cellValues.row.connectionStatus ? <StatusIcon status={cellValues.row.connectionStatus} /> : "";
    },
  },

  {
    field: "migrationStatus",
    headerName: "Migration Status",
    width: 200,
    sortable: false,
    renderCell: (cellValues: any) => {
      return !!cellValues.row.migrationStatus ? (
        <StatusIcon
          tooltip={
            cellValues.row.migrationStatus.includes("Err") ? cellValues.row.error : `${constStr.migrateStatus[cellValues.row.migrationStatus]}`
          }
          status={cellValues.row.migrationStatus.includes("Err") ? "Error" : cellValues.row.migrationStatus}
        />
      ) : (
        ""
      );
    },
  },
];

export const syslogForwardingColumn: GridColDef[] = [
  { field: "hostname", headerName: "Host Name", flex: 3, sortable: false, cellClassName: "font-mono text-xs" },
  { field: "port", headerName: "Port", flex: 1, sortable: false },
  {
    field: "enabled",
    headerName: "Enable",
    flex: 1,
    sortable: false,
    renderCell: (cellValues: any) => <Switch checked={cellValues.row.enabled} disabled />,
  },
  {
    field: "enabledList",
    headerName: "List Of Enabled Events",
    flex: 2,
    sortable: false,
    renderCell: (cellValues: any) => {
      const enabledList = cellValues.row.enabledList;
      if (typeof enabledList === "string") {
        const events = enabledList.split(",").map((e: string) => e.trim());
        const filteredEvents = events.filter((event: string) => event !== "NONE" && event !== "");
        return filteredEvents.length > 0 ? (
          <div className="flex items-center gap-1 flex-wrap">
            {filteredEvents.map((event: string) => (
              <Badge key={event} variant="outline" className="bg-muted px-1.5 py-0.5 rounded text-xs text-foreground border-transparent font-normal">{event}</Badge>
            ))}
          </div>
        ) : <Badge variant="outline" className="bg-muted px-1.5 py-0.5 rounded text-xs text-foreground border-transparent font-normal">None</Badge>;
      }
      return <Badge variant="outline" className="bg-muted px-1.5 py-0.5 rounded text-xs text-foreground border-transparent font-normal">None</Badge>;
    },
  },
  {
    field: "description",
    headerName: "Description",
    flex: 3,
    sortable: false,
    renderCell: (cellValues: any) => (
      <span className="text-foreground dark:text-slate-300">{cellValues.value}</span>
    ),
  },
];

const geoRegionCellLabels: Record<string, string> = {
  name: "Region",
  deviceCount: "Devices",
  disconnectedDeviceCount: "Disconnected Devices",
  offlineDeviceCount: "Offline Devices",
  kpiSyncErrorCount: "KPI Error",
};

const renderGeoRegionCell = (cellValues: any) => {
  const regionName = cellValues?.row?.name || "this region";
  const label = geoRegionCellLabels[cellValues.field] || "this field";
  const value = cellValues?.value ?? "-";

  return (
    <Tooltip title={`Click to filter Nodes by ${label} for ${regionName}`} arrow>
      <Typography
        component="span"
        sx={{
          color: "var(--primary)",
          cursor: "pointer",
          textDecoration: "underline",
          textDecorationColor: "transparent",
          textUnderlineOffset: "2px",
          "&:hover": { textDecorationColor: "currentColor" },
        }}
      >
        {value}
      </Typography>
    </Tooltip>
  );
};

export const geoRegionColumn: GridColDef[] = [
  {
    field: "name",
    headerName: "Region",
    width: 350,
    renderCell: renderGeoRegionCell,
  },
  { field: "deviceCount", headerName: "Devices", width: 200, renderCell: renderGeoRegionCell },
  { field: "disconnectedDeviceCount", headerName: "Disconnected Devices", width: 320, renderCell: renderGeoRegionCell },
  { field: "offlineDeviceCount", headerName: "Offline Devices", width: 320, renderCell: renderGeoRegionCell },
  { field: "kpiSyncErrorCount", headerName: "KPI Error", width: 320, renderCell: renderGeoRegionCell }
];

export const reportsColumn: GridColDef[] = [
  { field: "reportName", headerName: "Report", flex: 2, minWidth: 180 },
  {
    field: "geoRegion",
    headerName: "Region",
    flex: 1,
    minWidth: 120,
    renderCell: (cellValues: any) => <span className="text-sm text-muted-foreground">{cellValues.value}</span>,
  },
  { field: "type", headerName: "Type", flex: 1, minWidth: 120 },
  { field: "taskName", headerName: "Task", flex: 1, minWidth: 120 },
  {
    field: "createdTS",
    headerName: "Created Date",
    flex: 1,
    minWidth: 140,
    renderCell: (cellValues: any) => (cellValues?.createdTS <= 0 ? "" : dayjs(cellValues.row?.createdTS).format(DATEFORMATS.TS)),
  },
  {
    field: "actions",
    type: "actions",
    width: 120,
    renderCell: (cellValues: any) => (
      <div className="flex items-center gap-1">
        <DownloadReport rowData={cellValues.row} />
        <DeleteReport rowData={cellValues.row} />
      </div>
    ),
  },
];

export const reportPreviewPriorityColumns: Array<{ name: string; width: number }> = [
  { name: "device name", width: 150 },
  { name: "serial number", width: 150 },
  { name: "device ip", width: 150 },
  { name: "region", width: 150 },
  { name: "group", width: 150 },
  { name: "dm attribute", width: 300 },
  { name: "value", width: 150 },
];

export const createReportPreviewColumn = (header: string, width: number): GridColDef => {
  const normalizedHeader = header.trim().toLowerCase();
  const isDmAttributeColumn = normalizedHeader === "dm attribute";
  const isValueColumn = normalizedHeader === "value";
  const isRegionColumn = normalizedHeader === "region";

  return {
    field: header,
    headerName: header,
    width,
    sortable: false,
    renderCell: (cellValues: any) => {
      const cellValue = cellValues.value == null ? "" : String(cellValues.value);

      if (isDmAttributeColumn || isValueColumn) {
        return (
          <Tooltip title={cellValue} enterDelay={300} placement="bottom-start">
            <span
              tabIndex={cellValues.hasFocus ? 0 : -1}
              className="block w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs font-mono text-muted-foreground"
            >
              {convertSymbols(cellValue)}
            </span>
          </Tooltip>
        );
      }

      if (isRegionColumn) {
        return <span className="text-xs font-mono text-muted-foreground">{cellValue}</span>;
      }

      return <span className="text-xs font-mono text-muted-foreground">{convertSymbols(cellValue)}</span>;
    },
  };
};

export const filesColumn = (fileType: any): GridColDef[] => [
  { field: "fileName", headerName: "File Name", flex: 5, minWidth: 180 },
  {
    field: "createdAt",
    headerName: "Date",
    flex: 2,
    minWidth: 140,
    renderCell: (cellValues: any) => (cellValues?.createdAt <= 0 ? "" : dayjs(cellValues.row?.createdAt).format(DATEFORMATS.TS)),
  },
  { field: "actions", type: "actions", flex: 1, minWidth: 80, renderCell: (cellValues: any) => <DownloadFile rowData={cellValues.row} fileType={fileType} /> },
];

const handleCellsIcons = (field: string, value: number) => {
  const data: any = CellKpiIconState[field] || "";
  let result: string | null = null;

  if (data) {
    Object.keys(data).forEach((key: any) => {
      const condition = data[key].condition;
      const left = data[key].left_value;
      const right = data[key].right_value;
      if (
        (condition === ">=" && formatPercent(value || 0) >= right) ||
        (condition === "<=" && formatPercent(value || 0) <= right) ||
        (condition === "<" && formatPercent(value || 0) < right) ||
        (condition === "<X<" && formatPercent(value || 0) > left && formatPercent(value || 0) < right) ||
        (condition === ">X>" && formatPercent(value || 0) > right && formatPercent(value || 0) < left)
      ) {
        result = key;
      }
    });
  }
  if (result === "dark_error") {
    return <CellKpiStatus status={"Major"} value={value} />;
  } else if (result === "dark_alert") {
    return <CellKpiStatus status={"Minor"} value={value} />;
  } else if (result === "dark_success") {
    return <CellKpiStatus status={"Successful"} value={value} />;
  } else return `${formatPercent(value || 0).toString()} %`;
};

const handleCellsIconsPerformance = (field: string, value: number) => {
  const data: any = ExternalPerformanceIconState[field] || "";
  let result: string | null = null;

  if (data) {
    Object.keys(data).forEach((key: any) => {
      const condition = data[key].condition;
      const left = data[key].left_value;
      const right = data[key].right_value;
      if (
        (condition === ">=" && formatPercent(value || 0) >= right) ||
        (condition === "<=" && formatPercent(value || 0) <= right) ||
        (condition === "<" && formatPercent(value || 0) < right) ||
        (condition === "<X<" && formatPercent(value || 0) > left && formatPercent(value || 0) < right) ||
        (condition === ">X>" && formatPercent(value || 0) > right && formatPercent(value || 0) < left)
      ) {
        result = key;
      }
    });
  }
  if (result === "dark_error") {
    return <CellKpiStatus status={"Major"} value={value} />;
  } else if (result === "dark_alert") {
    return <CellKpiStatus status={"Minor"} value={value} />;
  } else if (result === "dark_success") {
    return <CellKpiStatus status={"Successful"} value={value} />;
  } else return `${formatPercent(value || 0).toString()} %`;
};

export const cellKpisColumns: GridColDef[] = [
  {
    field: "cell_name",
    headerName: "Cell name",
    minWidth: 160,
    flex: 2,
  },
  {
    field: "erab_establishment_sr", //percent
    headerName: "ERAB Establishment SR",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIcons("erab_establishment_sr", cellValues?.row?.erab_establishment_sr),
  },
  {
    field: "erab_establishment_success", //count
    headerName: "ERAB Establishment Success",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.erab_establishment_success || 0)),
  },
  {
    field: "erab_establishment_attempts", //count
    headerName: "ERAB Establishment Attempts",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.erab_establishment_attempts || 0)),
  },
  {
    field: "volte_establishment_sr", //percent
    headerName: "Volte Establishment SR",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIcons("volte_establishment_sr", cellValues?.row?.volte_establishment_sr),
  },
  {
    field: "volte_establishment_success", //count
    headerName: "Volte Establishment Success",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.volte_establishment_success || 0)),
  },
  {
    field: "volte_establishment_attempts", //count
    headerName: "Volte Establishment Attempts",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.volte_establishment_attempts || 0)),
  },
  {
    field: "rrc_connection_establishment_sr", //percent
    headerName: "RRC Connection Establishment SR",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIcons("rrc_connection_establishment_sr", cellValues?.row?.rrc_connection_establishment_sr),
  },
  {
    field: "rrc_connection_establishment_success", //count
    headerName: "RRC Connection Establishment Success",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.rrc_connection_establishment_success || 0)),
  },
  {
    field: "rrc_connection_establishment_attempts", //count
    headerName: "RRC Connection Establishment Attempts",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.rrc_connection_establishment_attempts || 0)),
  },
  {
    field: "erab_drop_rate", //percent
    headerName: "ERAB Drop Rate",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIcons("erab_drop_rate", cellValues?.row?.erab_drop_rate),
  },
  {
    field: "erab_drop_count", //count
    headerName: "ERAB Drop Count",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.erab_drop_count || 0)),
  },
  {
    field: "volte_drop_rate", //percent
    headerName: "VoLTE Drop Rate",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIcons("volte_drop_rate", cellValues?.row?.volte_drop_rate),
  },
  {
    field: "volte_drop_count", //count
    headerName: "VoLTE Drop Count",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.volte_drop_count || 0)),
  },
  {
    field: "dl_gtp_data_volume", //bytes
    headerName: "DL GTP data volume",
    width: 120,
    renderCell: (cellValues: any) => formatBytes(Number(cellValues?.row?.dl_gtp_data_volume || 0)),
  },
  {
    field: "ul_gtp_data_volume", //bytes
    headerName: "UL GTP Data Volume",
    width: 120,
    renderCell: (cellValues: any) => formatBytes(Number(cellValues?.row?.ul_gtp_data_volume || 0)),
  },
  {
    field: "hand_in_success_rate", //percent
    headerName: "HandIn SR",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIcons("hand_in_success_rate", cellValues?.row?.hand_in_success_rate),
  },
  {
    field: "hand_in_success", //count
    headerName: "HandIn Success",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.hand_in_success || 0)),
  },
  {
    field: "hand_in_attempts", //count
    headerName: "HandIn Attempts",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.hand_in_attempts || 0)),
  },
  {
    field: "inter_frequency_x1_ho", // percent
    headerName: "Inter Frequency X1-HO SR",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIcons("inter_frequency_x1_ho_sr", cellValues?.row?.inter_frequency_x1_ho),
  },
  {
    field: "inter_frequency_x1_ho_success", //count
    headerName: "Inter Frequency X1-HO Success",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.inter_frequency_x1_ho_success || 0)),
  },
  {
    field: "inter_frequency_x1_ho_attempts", //count
    headerName: "Inter Frequency X1-HO Attempts",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.inter_frequency_x1_ho_attempts || 0)),
  },
  {
    field: "lte_cell_availability", //percent
    headerName: "Lte Cell Availability",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIcons("lte_cell_availability", cellValues?.row?.lte_cell_availability),
  },
  {
    field: "max_endc_connected_ue_sessions", //count
    headerName: "Maximum ENDC Connected UE Sessions",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.max_endc_connected_ue_sessions || 0)),
  },
  {
    field: "sg_nb_addition_sr", //percent
    headerName: "SgNB Addition SR",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIcons("sg_nb_addition_sr", cellValues?.row?.sg_nb_addition_sr),
  },
  {
    field: "sg_nb_addition_success", //count
    headerName: "SgNB Addition Success",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.sg_nb_addition_success || 0)),
  },
  {
    field: "sg_nb_addition_attempts", //count
    headerName: "SgNB Addition Attempts",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.sg_nb_addition_attempts || 0)),
  },
];

export const nrCellKpisColumns: GridColDef[] = [
  {
    field: "cell_name",
    headerName: "Cell name",
    minWidth: 200,
    flex: 1,
  },
  {
    field: "sgnb_addition_sr", //percent
    headerName: "SgNB Addition SR",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIcons("sgnb_addition_sr", cellValues?.row?.sgnb_addition_sr),
  },
  {
    field: "sgnb_addition_attempts", //count
    headerName: "SgNB Addition Attempts",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.sgnb_addition_attempts || 0)),
  },
  {
    field: "sgnb_addition_request_ack", //count
    headerName: "SgNB Addition Request ACK",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.sgnb_addition_request_ack || 0)),
  },
  {
    field: "max_number_of_endc_sessions", //count
    headerName: "Max Number of  ENDC Sessions",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.max_number_of_endc_sessions || 0)),
  },
  {
    field: "endc_ue_session_drop_rate", //percent
    headerName: "ENDC UE Session Drop Rate",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIcons("endc_ue_session_drop_rate", cellValues?.row?.endc_ue_session_drop_rate),
  },
  {
    field: "endc_ue_session_drop_counts", //count
    headerName: "ENDC UE Session Drop Count",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.endc_ue_session_drop_counts || 0)),
  },
  {
    field: "dl_s1u_data_volume", //bytes
    headerName: "DL S1-U data volume",
    width: 120,
    renderCell: (cellValues: any) => formatBytes(Number(cellValues?.row?.dl_s1u_data_volume || 0)),
  },
  {
    field: "ul_s1u_data_volume", //bytes
    headerName: "UL S1-U Data Volume",
    width: 120,
    renderCell: (cellValues: any) => formatBytes(Number(cellValues?.row?.ul_s1u_data_volume || 0)),
  },
  {
    field: "intra_cu_handover_success_rate", //percent
    headerName: "Intra CU Handover SR",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIcons("intra_cu_handover_success_rate", cellValues?.row?.intra_cu_handover_success_rate),
  },
  {
    field: "intra_cu_handover_success", //count
    headerName: "Intra CU Handover Success",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.intra_cu_handover_success || 0)),
  },
  {
    field: "intra_cu_handover_attempts", //count
    headerName: "Intra CU Handover Attempts",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.intra_cu_handover_attempts || 0)),
  },
  {
    field: "nr_cell_availability", //percent
    headerName: "NR Cell Availability",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIcons("nr_cell_availability", cellValues?.row?.nr_cell_availability),
  },
];

// EXTERNAL PERFORMANCE LTE COLUMNS

export const externalPerformanceLTEColumns: GridColDef[] = [
  {
    field: "geoRegion",
    headerName: "Region",
    minWidth: 200,
    flex: 1,
    renderCell: (cellValues: any) => {
      const value = cellValues?.row?.geoRegion;
      return (
        <span className="group/devlink inline-flex items-center gap-1 min-w-0 text-link cursor-pointer">
          <span className="font-medium truncate group-hover/devlink:underline">{value}</span>
        </span>
      );
    },
  },
  {
    field: "numOfSites", //count
    headerName: "Number of Sites",
    width: 120,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.numOfSites || 0)),
  },
  {
    field: "dataAccessibilityPercent", //percent
    headerName: "Data Accessibility %",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("dataAccessibilityPercent", cellValues?.row?.dataAccessibilityPercent),
  },
  {
    field: "volteAccessibilityPercent", //percent
    headerName: "Volte Accessibility %",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("volteAccessibilityPercent", cellValues?.row?.volteAccessibilityPercent),
  },
  {
    field: "dataRetainabilityPercent", //percent
    headerName: "Data Retainability %",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("dataRetainabilityPercent", cellValues?.row?.dataRetainabilityPercent),
  },
  {
    field: "volteRetainabilityPercent", //percent
    headerName: "Volte Retainability %",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("volteRetainabilityPercent", cellValues?.row?.volteRetainabilityPercent),
  },
  {
    field: "s1SuccessRatePercent", //percent
    headerName: "S1 Success Rate %",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("s1SuccessRatePercent", cellValues?.row?.s1SuccessRatePercent),
  },
  {
    field: "macroApHandinPercent", //percent
    headerName: "Macro Ap Handin %",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("macroApHandinPercent", cellValues?.row?.macroApHandinPercent),
  },
  {
    field: "apMacroIntraHandoutPercent", //percent
    headerName: "AP Macro Intra Handout %",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("apMacroIntraHandoutPercent", cellValues?.row?.apMacroIntraHandoutPercent),
  },
  {
    field: "apMacroInterFreqHandoutPercent", //percent
    headerName: "AP Macro Inter Frequency Handout %",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("apMacroInterFreqHandoutPercent", cellValues?.row?.apMacroInterFreqHandoutPercent),
  },
  {
    field: "intraEranInterFreqPercent", //percent
    headerName: "Intra Eran Inter Frequency %",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("intraEranInterFreqPercent", cellValues?.row?.intraEranInterFreqPercent),
  },
  {
    field: "intraEranIntraFreqPercent", //percent
    headerName: "Intra Eran Intra Frequency %",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("intraEranIntraFreqPercent", cellValues?.row?.intraEranIntraFreqPercent),
  },
  {
    field: "x1HoIntraFreqPercent", //percent
    headerName: "X1 Ho Intra Frequency %",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("x1HoIntraFreqPercent", cellValues?.row?.x1HoIntraFreqPercent),
  },
  {
    field: "x1HoInterFreqPercent", //percent
    headerName: "X1 Ho Inter Frequency %",
    width: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("x1HoInterFreqPercent", cellValues?.row?.x1HoInterFreqPercent),
  },
];

export const activityPerformanceColumns: GridColDef[] = [
  {
    field: "hostName",
    headerName: "Host Name",
    flex: 2,
    minWidth: 180,
    renderCell: (cellValues: any) => {
      const value = cellValues?.row?.hostName;
      return (
        <span className="group/devlink inline-flex items-center gap-1 min-w-0 text-link cursor-pointer">
          <span className="font-medium truncate group-hover/devlink:underline">{value}</span>
          <Icon
            name="open_in_new"
            size={14}
            className="shrink-0 opacity-0 group-hover/devlink:opacity-70 transition-opacity text-muted-foreground"
          />
        </span>
      );
    },
  },
  {
    field: "geoRegionName",
    headerName: "Region",
    flex: 1,
    minWidth: 120,
  },
  {
    field: "dataAccessibilityPercent",
    headerName: "Data Accessibility %",
    flex: 1,
    minWidth: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("dataAccessibilityPercent", cellValues?.row?.dataAccessibilityPercent),
  },
  {
    field: "volteAccessibilityPercent",
    headerName: "Volte Accessibility %",
    flex: 1,
    minWidth: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("volteAccessibilityPercent", cellValues?.row?.volteAccessibilityPercent),
  },
  {
    field: "dataRetainabilityPercent",
    headerName: "Data Retainability %",
    flex: 1,
    minWidth: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("dataRetainabilityPercent", cellValues?.row?.dataRetainabilityPercent),
  },
  {
    field: "volteRetainabilityPercent",
    headerName: "Volte Retainability %",
    flex: 1,
    minWidth: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("volteRetainabilityPercent", cellValues?.row?.volteRetainabilityPercent),
  },
  {
    field: "s1SuccessRatePercent",
    headerName: "S1 Success Rate %",
    flex: 1,
    minWidth: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("s1SuccessRatePercent", cellValues?.row?.s1SuccessRatePercent),
  },
  {
    field: "macroApHandinPercent",
    headerName: "Macro AP Handin %",
    flex: 1,
    minWidth: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("macroApHandinPercent", cellValues?.row?.macroApHandinPercent),
  },
  {
    field: "apMacroIntraHandoutPercent",
    headerName: "AP Macro Intra Handout %",
    flex: 1,
    minWidth: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("apMacroIntraHandoutPercent", cellValues?.row?.apMacroIntraHandoutPercent),
  },
  {
    field: "apMacroInterFreqHandoutPercent",
    headerName: "AP Macro Inter Frequency Handout %",
    flex: 1,
    minWidth: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("apMacroInterFreqHandoutPercent", cellValues?.row?.apMacroInterFreqHandoutPercent),
  },
  {
    field: "intraEranInterFreqPercent",
    headerName: "Intra Eran Inter Frequency %",
    flex: 1,
    minWidth: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("intraEranInterFreqPercent", cellValues?.row?.intraEranInterFreqPercent),
  },
  {
    field: "intraEranIntraFreqPercent",
    headerName: "Intra Eran Intra Frequency %",
    flex: 1,
    minWidth: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("intraEranIntraFreqPercent", cellValues?.row?.intraEranIntraFreqPercent),
  },
  {
    field: "x1HoIntraFreqPercent",
    headerName: "X1 HO Intra Frequency %",
    flex: 1,
    minWidth: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("x1HoIntraFreqPercent", cellValues?.row?.x1HoIntraFreqPercent),
  },
  {
    field: "x1HoInterFreqPercent",
    headerName: "X1 HO Inter Frequency %",
    flex: 1,
    minWidth: 120,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("x1HoInterFreqPercent", cellValues?.row?.x1HoInterFreqPercent),
  },
];


export const nrRegionPerformanceColumns: GridColDef[] = [
  {
    field: "geoRegion",
    headerName: "Region",
    minWidth: 200,
    flex: 1,
    renderCell: (cellValues: any) => {
      const value = cellValues?.row?.geoRegion;
      return (
        // <div
        //   style={{
        //     color: "var(--primary)",
        //     fontWeight: "bold",
        //     cursor: "pointer",
        //   }}
        // >
        //   {value}
        // </div>
        <span className="group/devlink inline-flex items-center gap-1 min-w-0 text-link cursor-pointer">
          <span className="font-medium truncate group-hover/devlink:underline">{value}</span>
          <Icon
            name="open_in_new"
            size={14}
            className="shrink-0 opacity-0 group-hover/devlink:opacity-70 transition-opacity text-muted-foreground"
          />
        </span>
      );
    },
  },
  {
    field: "numOfSites", //count
    headerName: "No. of Sites",
    width: 160,
    renderCell: (cellValues: any) => formatCounts(Number(cellValues?.row?.numOfSites || 0)),
  },
  {
    field: "endcDataAccessibilityPercent", //percent
    headerName: "ENDC Data Accessibility %",
    width: 200,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("endcDataAccessibilityPercent", cellValues?.row?.endcDataAccessibilityPercent),
  },
  {
    field: "endcDataRetainabilityPercent", //percent
    headerName: "ENDC Data Retainability %",
    width: 200,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("endcDataRetainabilityPercent", cellValues?.row?.endcDataRetainabilityPercent),
  },
  {
    field: "endcUeRetainabilityPercent", //percent
    headerName: "ENDC UE Retainability %",
    width: 200,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("endcUeRetainabilityPercent", cellValues?.row?.endcUeRetainabilityPercent),
  },
  {
    field: "endcSgNBAdditionSuccessRate", //percent
    headerName: "ENDC SGNB Addition Success Rate",
    width: 200,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("endcSgNBAdditionSuccessRate", cellValues?.row?.endcSgNBAdditionSuccessRate),
  },
  {
    field: "endcIntraCUIntraFreqHandoverSuccessRate", //percent
    headerName: "ENDC Intra CU Intra Frequency Handover Success Rate",
    width: 200,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("endcIntraCUIntraFreqHandoverSuccessRate", cellValues?.row?.endcIntraCUIntraFreqHandoverSuccessRate),
  },
  {
    field: "endcIntraCUInterFreqHandoverSuccessRate", //percent
    headerName: "ENDC Intra CU Inter Frequency Handover Success Rate",
    width: 200,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("endcIntraCUInterFreqHandoverSuccessRate", cellValues?.row?.endcIntraCUInterFreqHandoverSuccessRate),
  },

];

export const activityNRPerformanceColumns: GridColDef[] = [
  {
    field: "hostName",
    headerName: "Host Name",
    flex: 2,
    minWidth: 180,
    renderCell: (cellValues: any) => {
      const value = cellValues?.row?.hostName;
      return (
        <span className="group/devlink inline-flex items-center gap-1 min-w-0 text-link cursor-pointer">
          <span className="font-medium truncate group-hover/devlink:underline">{value}</span>
          <Icon
            name="open_in_new"
            size={14}
            className="shrink-0 opacity-0 group-hover/devlink:opacity-70 transition-opacity text-muted-foreground"
          />
        </span>
      );
    },
  },
  {
    field: "geoRegionName",
    headerName: "Region",
    flex: 1,
    minWidth: 120,
  },
  {
    field: "endcDataAccessibilityPercent",
    headerName: "ENDC Data Accessibility %",
    flex: 1,
    minWidth: 140,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("endcDataAccessibilityPercent", cellValues?.row?.endcDataAccessibilityPercent),
  },
  {
    field: "endcDataRetainabilityPercent",
    headerName: "ENDC Data Retainability %",
    flex: 1,
    minWidth: 140,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("endcDataRetainabilityPercent", cellValues?.row?.endcDataRetainabilityPercent),
  },
  {
    field: "endcUeRetainabilityPercent",
    headerName: "ENDC UE Retainability %",
    flex: 1,
    minWidth: 140,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("endcUeRetainabilityPercent", cellValues?.row?.endcUeRetainabilityPercent),
  },
  {
    field: "endcSgNBAdditionSuccessRate",
    headerName: "ENDC SGNB Addition Success Rate",
    flex: 1,
    minWidth: 140,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("endcSgNBAdditionSuccessRate", cellValues?.row?.endcSgNBAdditionSuccessRate),
  },
  {
    field: "endcIntraCUIntraFreqHandoverSuccessRate",
    headerName: "ENDC Intra CU Intra Frequency Handover Success Rate",
    flex: 1,
    minWidth: 140,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("endcIntraCUIntraFreqHandoverSuccessRate", cellValues?.row?.endcIntraCUIntraFreqHandoverSuccessRate),
  },
  {
    field: "endcIntraCUInterFreqHandoverSuccessRate",
    headerName: "ENDC Intra CU Inter Frequency Handover Success Rate",
    flex: 1,
    minWidth: 140,
    renderCell: (cellValues: any) => handleCellsIconsPerformance("endcIntraCUInterFreqHandoverSuccessRate", cellValues?.row?.endcIntraCUInterFreqHandoverSuccessRate),
  },
];
export const sizeChartColumns: GridColDef[] = [
  {
    field: "name",
    headerName: "Name",
    width: 300,
  },
  {
    field: "green",
    renderHeader: (params: GridColumnHeaderParams) => <strong><StatusIcon status={"Optimal"} /></strong>,
    width: 150,
  },
  {
    field: "yellow",
    renderHeader: (params: GridColumnHeaderParams) => <strong><StatusIcon status={"Suboptimal"} /></strong>,
    width: 150,
  },
  {
    field: "red",
    renderHeader: (params: GridColumnHeaderParams) => <strong><StatusIcon status={"Degraded"} /></strong>,
    width: 150,
  },
];


export const ThresholdCrossingRuleColumns: GridColDef[] = [
  { field: "objectKey", headerName: "Sequence ID", width: 120 },
  { field: "kpiName", headerName: "KPI", width: 150 },
  { field: "kpiType", headerName: "KPI Type", width: 150 },
  { field: "thresholdAbove", headerName: "Alert When Above", width: 150 },
  { field: "thresholdBelow", headerName: "Alert When Low", width: 150 },
  { field: "consecutiveSamples", headerName: "Consecutive Samples", width: 150 },
];

export const thresholdDeviceColumns: GridColDef[] = [
  { field: "name", headerName: "Device", width: 250, sortable: false },
  { field: "nodeType", headerName: "Device Type", width: 250, sortable: false },
  { field: "groupKey", headerName: "Group", width: 250, sortable: false },
  { field: "parentKey", headerName: "Region", width: 250, sortable: false },
]
export const devicesColumns = (fileType: any): GridColDef[] => [
  {
    field: "name",
    headerName: "Device",
    width: 490,
    renderCell: (cellValues: any) => {
      return `${cellValues.row.name} (${cellValues.row.ipAddress})`;
    },
  },
  { field: "siteGroupName", headerName: "Group", width: 180 },
  { field: "geoRegionName", headerName: "Region", width: 300 },

];

export const devicesNotoficationGroupColumns: GridColDef[] = [
  {
    field: "name",
    headerName: "Device",
    flex: 3,
    renderCell: (cellValues: any) => {
      return (
        <span className="group/devlink inline-flex items-center gap-1 min-w-0 text-link cursor-pointer">
          <span className="font-medium truncate group-hover/devlink:underline">{`${cellValues.row.name} (${cellValues.row.ipAddress})`}</span>
          <Icon
            name="open_in_new"
            size={14}
            className="shrink-0 opacity-0 group-hover/devlink:opacity-70 transition-opacity text-muted-foreground"
          />
        </span>
      );
    },
  },
  { field: "geoRegionName", headerName: "Region", flex: 2 },
  { field: "siteGroupName", headerName: "Group", flex: 1 },

];


export const adddevicesNotoficationGroupColumns: GridColDef[] = [
  {
    field: "name",
    headerName: "Device",
    width: 250,
    renderCell: (cellValues: any) => {
      return (
        <span className="group/devlink inline-flex items-center gap-1 min-w-0 text-link cursor-pointer">
          <span className="font-medium truncate group-hover/devlink:underline">{`${cellValues.row.name} (${cellValues.row.ipAddress})`}</span>
          <Icon
            name="open_in_new"
            size={14}
            className="shrink-0 opacity-0 group-hover/devlink:opacity-70 transition-opacity text-muted-foreground"
          />
        </span>
      );
    },
  },
  { field: "geoRegionName", headerName: "Region", width: 150 },
  { field: "siteGroupName", headerName: "Group", width: 150 },

];


export const devicesLabelColumns: GridColDef[] = [
  {
    field: "name",
    headerName: "Device",
    flex: 3,
    renderCell: (cellValues: any) => {
      return (
        <span className="group/devlink inline-flex items-center gap-1 min-w-0 text-link cursor-pointer">
          <span className="font-medium truncate group-hover/devlink:underline">{`${cellValues.row.name} (${cellValues.row.ipAddress})`}</span>
          <Icon
            name="open_in_new"
            size={14}
            className="shrink-0 opacity-0 group-hover/devlink:opacity-70 transition-opacity text-muted-foreground"
          />
        </span>
      );
    },
  },
  { field: "geoRegionName", headerName: "Region", flex: 2 },
  { field: "siteGroupName", headerName: "Group", flex: 1 },
  { field: "labelCount", headerName: "Total Labels", sortable: false, flex: 1 }
]

export const adddevicesLabelColumns: GridColDef[] = [
  {
    field: "name",
    headerName: "Device",
    width: 250,
    renderCell: (cellValues: any) => {
      return (
        <span className="group/devlink inline-flex items-center gap-1 min-w-0 text-link cursor-pointer">
          <span className="font-medium truncate group-hover/devlink:underline">{`${cellValues.row.name} (${cellValues.row.ipAddress})`}</span>
          <Icon
            name="open_in_new"
            size={14}
            className="shrink-0 opacity-0 group-hover/devlink:opacity-70 transition-opacity text-muted-foreground"
          />
        </span>
      );
    },
  },
  { field: "geoRegionName", headerName: "Region", width: 150 },
  { field: "siteGroupName", headerName: "Group", width: 150 },
  { field: "labelCount", headerName: "Total Labels", sortable: false, width: 100 }
]

export const thresholdProfilesColumns: GridColDef[] = [
  {
    field: "name",
    headerName: "Profile",
    flex: 3,
    minWidth: 180,
    renderCell: (cellValues: any) => {
      return (
        <span className="group/devlink inline-flex items-center gap-1 min-w-0 text-link cursor-pointer">
          <span className="font-medium truncate group-hover/devlink:underline">{cellValues.row.name}</span>
          <Icon
            name="open_in_new"
            size={14}
            className="shrink-0 opacity-0 group-hover/devlink:opacity-70 transition-opacity text-muted-foreground"
          />
        </span>
      );
    },
  },
  {
    field: "enabled",
    headerName: "Status",
    flex: 1,
    minWidth: 120,
    sortable: false,
    renderCell: (cellValues: any) => {
      const isEnabled = Boolean(cellValues.row?.enabled);
      return (
        <Chip
          size="small"
          label={isEnabled ? "Enabled" : "Disabled"}
          color={isEnabled ? "primary" : "default"}
          sx={isEnabled ? { backgroundColor: "var(--primary)", color: "var(--primary-foreground)" } : { backgroundColor: "var(--muted)", color: "var(--muted-foreground)" }}
        />
      );
    },
  },
  {
    field: "action",
    headerName: "Actions",
    flex: 2,
    minWidth: 160,
    renderCell: (cellValues: any) => {
      return `${cellValues.row.actions?.[0]?.details?.join(",") ?? ""}`;
    }
  },
  {
    field: "schedules",
    headerName: "No. of Schedules",
    flex: 1,
    minWidth: 120,
    sortable: false,
    renderCell: (cellValues: any) => `${cellValues.row.schedules?.length ?? 0}`,
  },
  {
    field: "rules",
    headerName: "No. of Rules",
    flex: 1,
    minWidth: 120,
    sortable: false,
    renderCell: (cellValues: any) => `${cellValues.row.rules?.length ?? 0}`,
  },
];



const renderTriggeredThreshold = (row: any) => {
  const above = row?.thresholdAbove;
  const below = row?.thresholdBelow;
  if (above !== null && above !== undefined) {
    return `>${above}`;
  }
  if (below !== null && below !== undefined) {
    return `<${below}`;
  }
  return "N/A";
};

const renderStateValue = (row: any) => {
  if (!row?.cleared) {
    return "Active";
  }
  if (row?.clearedBy === "USER") {
    const label = "Cleared manually";
    const username = row?.clearedByUserName;
    if (!username) {
      return label;
    }
    return (
      <Tooltip title={`Cleared by ${username}`}>
        <span>{label}</span>
      </Tooltip>
    );
  }
  return "Cleared";
};

export const thresholdCrossingColumns = ({
  showDevice,
  onClearRequested,
  onCellRequested,
  canClear = true,
}: {
  showDevice: boolean;
  onClearRequested?: (id: number) => void;
  onCellRequested?: (row: any) => void;
  canClear?: boolean;
}): GridColDef[] => {
  const columns: GridColDef[] = [];

  if (showDevice) {
    columns.push({
      field: "device",
      headerName: "Device",
      flex: 2,
      minWidth: 160,
      renderCell: (cellValues: any) => {
        if (!cellValues.row?.device) {
          return "";
        }
        const value = cellValues.row.device.modelName === "migration"
          ? cellValues.row.device.objectKey
          : cellValues.row.device.name;
        return (
          <span className="group/devlink inline-flex items-center gap-1 min-w-0 text-link cursor-pointer">
            <span className="font-medium truncate group-hover/devlink:underline">{value}</span>
            <Icon
              name="open_in_new"
              size={14}
              className="shrink-0 opacity-0 group-hover/devlink:opacity-70 transition-opacity text-muted-foreground"
            />
          </span>
        );
      },
      sortable: true,
    });
  }

  columns.push(
    {
      field: "cellName",
      headerName: "Cell",
      flex: 1,
      minWidth: 130,
      sortable: true,
      renderCell: (cellValues: any) => {
        const cellValue = cellValues.row?.cellName || cellValues.row?.cellId || "";
        if (!cellValue || !onCellRequested) {
          return <span className="text-sm text-muted-foreground">{cellValue}</span>;
        }
        return (
          <span
            role="button"
            tabIndex={0}
            className="group/devlink inline-flex items-center gap-1 min-w-0 text-link cursor-pointer"
            onClick={(event) => {
              event.stopPropagation();
              onCellRequested(cellValues.row);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.stopPropagation();
                onCellRequested(cellValues.row);
              }
            }}
          >
            <span className="font-medium truncate group-hover/devlink:underline">{cellValue}</span>
            <Icon
              name="open_in_new"
              size={14}
              className="shrink-0 opacity-0 group-hover/devlink:opacity-70 transition-opacity text-muted-foreground"
            />
          </span>
        );
      },
    },
    { field: "region", headerName: "Region", flex: 1, minWidth: 120, sortable: true },
    { field: "profileName", headerName: "Profile Name", flex: 1, minWidth: 120, sortable: true },
    { field: "ruleName", headerName: "KPI", flex: 1, minWidth: 120, sortable: true },
    {
      field: "triggeredThreshold",
      headerName: "Triggered Threshold",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (cellValues: any) => {
        return <span className="text-sm text-muted-foreground">{renderTriggeredThreshold(cellValues.row)}</span>;
      }
    },
    { field: "lastValue", headerName: "Last Triggered Value", flex: 1, minWidth: 120, sortable: true },
    {
      field: "consecutiveAlertsCount",
      headerName: "Consecutive Alerts",
      flex: 1,
      minWidth: 100,
      sortable: true,
      renderCell: (cellValues: any) => cellValues?.value ?? 0,
    },
    {
      field: "triggeredAt",
      headerName: "Triggered At",
      renderCell: (cellValues: any) => {
        return <span className="text-sm text-muted-foreground">{formatUtcTimestamp(cellValues?.value)}</span>;
      },
      flex: 1,
      minWidth: 120,
      sortable: true,
    },
    {
      field: "clearedAt",
      headerName: "Cleared At",
      renderCell: (cellValues: any) => formatUtcTimestamp(cellValues?.value),
      flex: 1,
      minWidth: 120,
      sortable: true,
    },
    {
      field: "state",
      headerName: "State",
      flex: 1,
      minWidth: 100,
      sortable: true,
      renderCell: (cellValues: any) => renderStateValue(cellValues.row),
    },
    ...(canClear
      ? [
        {
          field: "clear",
          headerName: "",
          width: 160,
          sortable: false,
          renderCell: (cellValues: any) => {
            if (cellValues.row?.cleared) {
              return "";
            }
            const id = Number(cellValues?.row?.id);
            if (!Number.isFinite(id)) {
              return "";
            }
            return <ThresholdCrossingClearButton id={id} onClearRequested={onClearRequested} />;
          },
        },
      ]
      : [])
  );

  return columns;
};

export const thresholdDevicesColumns: GridColDef[] = [
  {
    field: "name",
    headerName: "Device",
    flex: 5,
    renderCell: (cellValues: any) => {
      return (
        <span className="group/devlink inline-flex items-center gap-1 min-w-0 text-link cursor-pointer">
          <span className="font-medium truncate group-hover/devlink:underline">{`${cellValues.row.name} (${cellValues.row.ipAddress})`}</span>
          <Icon
            name="open_in_new"
            size={14}
            className="shrink-0 opacity-0 group-hover/devlink:opacity-70 transition-opacity text-muted-foreground"
          />
        </span>
      );
    },
  },
  {
    field: "nodeType",
    headerName: "Type",
    flex: 1,
    renderCell: (cellValues: any) =>
      cellValues.row.nodeType ? (
        <Badge variant="secondary">{cellValues.row.nodeType}</Badge>
      ) : null,
  },

  {
    field: "geoRegionName", headerName: "Region", flex: 3,
    renderCell: (cellValues: any) => {
      const value = cellValues.row?.geoRegionName ?? "";
      return (
        <span className="align-middle text-muted-foreground">
          {value}
        </span>
      );
    }
  },
  {
    field: "siteGroupName", headerName: "Group", flex: 3,
    renderCell: (cellValues: any) => {
      const value = cellValues.row?.siteGroupName ?? "";
      return (
        <span className="align-middle text-muted-foreground">
          {value}
        </span>
      );
    }
  },
  {
    field: "status",
    headerName: "Status",
    flex: 2,
    renderCell: (cellValues: any) => {
      return <StatusIcon status={cellValues.row.status.toString() === DEVICESTATUS.TIMEOUT ? "Disconnected" : "Connected"} />;
    },
  }

];

export const addthresholdDevicesColumns: GridColDef[] = [
  {
    field: "name",
    headerName: "Device",
    width: 250,
    renderCell: (cellValues: any) => {
      return (
        <span className="group/devlink inline-flex items-center gap-1 min-w-0 text-link cursor-pointer">
          <span className="font-medium truncate group-hover/devlink:underline">{`${cellValues.row.name} (${cellValues.row.ipAddress})`}</span>
          <Icon
            name="open_in_new"
            size={14}
            className="shrink-0 opacity-0 group-hover/devlink:opacity-70 transition-opacity text-muted-foreground"
          />
        </span>
      );
    },
  },
  { field: "geoRegionName", headerName: "Region", width: 150 },
  { field: "siteGroupName", headerName: "Group", width: 150 },
];

// ALARMS OVERVIEW COLUMNS 

export const disconnectedDeviceColumns: GridColDef[] = [
  {
    field: "hostName",
    headerName: "Device",
    width: 215,
    renderCell: (cellValues: any) => {
      const valueToDisplay = cellValues.row?.hostName ?? cellValues.row?.deviceName;
      return <span style={{ color: "var(--link)" }}>{valueToDisplay || ""}</span>;
    },
  },
  { field: "geoRegionName", headerName: "Region", width: 215 },
  {
    field: "occuredDateAndTimeMs",
    headerName: "Time Occured",
    width: 215,
    renderCell: (cellValues: any) => convertEpochToGMT(cellValues?.value, DATEFORMATS.TS),
  },

];

export const disconnectedRadioNodeColumns: GridColDef[] = [
  {
    field: "name",
    headerName: "Device",
    width: 200,
    sortable: false,
    renderCell: (cellValues: any) => {
      const valueToDisplay = cellValues.row?.name;
      return <span style={{ color: "var(--link)" }}>{valueToDisplay || ""}</span>;
    },
  },
  {
    field: "serialNumber",
    headerName: "Serial Number",
    width: 150,
    sortable: false,
  },
  { field: "totalRadioNode", headerName: "Total", width: 100, sortable: false, },
  { field: "connectedRadioNodes", headerName: "Connected", width: 100, sortable: false, },
  { field: "disconnectedRadioNodes", headerName: "Disconnected", width: 100, sortable: false, },
  {
    field: "disconnectedPercent",
    headerName: "Disconnected %",
    width: 120,
    sortable: false,
    renderCell: (cellValues: any) => {
      const value = cellValues.row?.disconnectedPercent;
      if (value == null) return "";
      return `${value}%`;
    },
  },
];

// RADIATING AND NON RADIATING CELLS COLUMNS

export const cellsRadiatingColumns: GridColDef[] = [
  {
    field: "hostname",
    headerName: "Device Name",
    width: 215,
    renderCell: (cellValues: any) => (
      <span style={{ color: "var(--link)" }}>{cellValues.value || ""}</span>
    ),
  },
  { field: "cell", headerName: "Cell Name", width: 215 },
  { field: "cellId", headerName: "Cell ID", width: 215 },
  { field: "geoRegion", headerName: "Region", width: 215 },
  { field: "radioNode", headerName: "Radio Node", width: 215 },
];

export const cellsNonRadiatingColumns: GridColDef[] = [
  {
    field: "hostName",
    headerName: "Device Name",
    width: 215,
    renderCell: (cellValues: any) => {
      const valueToDisplay = cellValues.row?.hostName ?? cellValues.row?.deviceName;
      return <span style={{ color: "var(--link)" }}>{valueToDisplay || ""}</span>;
    },
  },
  { field: "cellname", headerName: "Cell Name", width: 215 },
  { field: "cellId", headerName: "Cell ID", width: 215 },
  { field: "geoRegionName", headerName: "Region", width: 215 },
  {
    field: "occuredDateAndTimeMs",
    headerName: "Time Occured",
    width: 215,
    renderCell: (cellValues: any) => convertEpochToGMT(cellValues?.value, DATEFORMATS.TS),
  },
  { field: "radionode", headerName: "Radio Node", width: 215 },
];

export const majorAlarmColumns: GridColDef[] = [
  {
    field: "hostName",
    headerName: "Device",
    width: 220,
    renderCell: (cellValues: any) => {
      return <span style={{ color: "var(--link)" }}>{cellValues.value || ""}</span>;
    },
  },
  {
    field: "geoRegionName",
    headerName: "Region",
    width: 220,
    renderCell: (cellValues: any) => {
      const valueToDisplay = cellValues.row?.geoRegionName ?? "";
      return valueToDisplay || "";
    },
  },
  {
    field: "occuredDateAndTimeMs",
    headerName: "Time Occurred",
    width: 220,
    renderCell: (cellValues: any) => convertEpochToGMT(cellValues?.value, DATEFORMATS.TS),
  },
];

export const topOffenderColumns: GridColDef[] = [
  {
    field: "name",
    headerName: "Device",
    width: 180,
    sortable: false,
    renderCell: (cellValues: any) => {
      const displayValue = cellValues.row.modelName === "migration" ? cellValues.row.objectKey : cellValues.row.name;
      return <span style={{ color: "var(--link)" }}>{displayValue}</span>;
    },
  },
  {
    field: "geoRegionName",
    headerName: "Region",
    width: 180,
    sortable: false,
  },
  {
    field: "nodeType",
    headerName: "Type",
    width: 180,
    sortable: false,
    renderCell: (cellValues: any) => <Chip size="small" label={cellValues.row.nodeType} />,
  },
  {
    field: "status",
    headerName: "Status",
    width: 180,
    sortable: false,
    renderCell: (cellValues: any) => {
      return <StatusIcon status={cellValues.row.status.toString() === DEVICESTATUS.TIMEOUT ? "Disconnected" : "Connected"} />;
    },
  },
  {
    field: "alarms",
    headerName: "Alarms",
    width: 180,
    sortable: false,
    renderCell: (cellValues: any) => {
      return <AlarmsStatus tableRow={cellValues.row} />;
    },
  },
  {
    field: "configurationStatus",
    headerName: "Configuration Status",
    width: 180,
    sortable: false,
  },
  { field: "serialNumber", headerName: "Serial Number", width: 190 },
  {
    field: "ipAddress",
    headerName: "IP Address",
    width: 190,
    sortable: false,
  },
];

// Inventory Dashboard - Radio Nodes Columns
export const InventoryRadioNodeColumns: GridColDef[] = [
  {
    field: "radioNodeName",
    headerName: "Radio node",
    width: 180,
  },
  {
    field: "index",
    headerName: "Index",
    width: 100,
  },
  {
    field: "geoRegion",
    headerName: "Region",
    width: 150,
  },
  {
    field: "description",
    headerName: "Description",
    width: 250,
  },
  {
    field: "hostname",
    headerName: "Host",
    width: 180,
    renderCell: (cellValues: any) => {
      const hostname = cellValues.row.hostname;
      const displayValue = hostname ? hostname.split(':')[0] : '';
      return (
        <Typography
          sx={{
            color: '#1976d2',
            cursor: 'pointer',
            fontWeight: 'normal',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          {displayValue}
        </Typography>
      );
    },
  },
  {
    field: "enabled",
    headerName: "Enabled",
    width: 120,
    align: "center",
    headerAlign: "center",
    renderCell: (cellValues: any) => {
      const enabled = cellValues.row.enabled;
      return enabled ? (
        <CheckCircleIcon sx={{ color: '#4ADE80', fontSize: 16 }} />
      ) : (
        <CancelIcon sx={{ color: '#94A3B8', fontSize: 16 }} />
      );
    },
  },
  {
    field: "state",
    headerName: "State",
    width: 130,
    renderCell: (cellValues: any) => (
      <Chip
        size="small"
        label={cellValues.row.state}
        sx={{
          backgroundColor: "var(--secondary)",
          color: "var(--secondary-foreground)",
          border: "none",
          fontWeight: "var(--font-weight-medium, 500)",
          fontSize: "var(--font-size-xs)",
          height: 26,
          borderRadius: "calc(var(--radius) - 2px)",
          "& .MuiChip-label": { px: 3 },
        }}
      />
    ),
  },
  {
    field: "nrCells",
    headerName: "Cells",
    width: 200,
    sortable: false,
    renderCell: (cellValues: any) => {
      const cells = (cellValues.row.nrCells || []) as string[];
      const filteredCells = cells.filter((cell: string) => !/:NRCell:/i.test(cell));
      if (filteredCells.length <= 1) {
        return <span>{filteredCells[0] ?? ''}</span>;
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '4px 0' }}>
          {filteredCells.map((cell, i) => (
            <span key={i} style={{ lineHeight: 1.4 }}>{cell}</span>
          ))}
        </div>
      );
    },
  },
  {
    field: "ethernetId",
    headerName: "Ethernet ID",
    width: 180,
  },
  {
    field: "model",
    headerName: "Model",
    width: 150,
  },
  {
    field: "serialNumber",
    headerName: "Serial number",
    width: 180,
  },
];

// Inventory Dashboard - Cells Columns
export const InventoryCellColumns: GridColDef[] = [
  {
    field: "cell",
    headerName: "Cell",
    width: 200,

  },
  {
    field: "cellId",
    headerName: "Cell ID",
    width: 100,
  },
  {
    field: "technology",
    headerName: "Technology",
    width: 120,
  },
  {
    field: "geoRegion",
    headerName: "Region",
    width: 150,
  },
  {
    field: "description",
    headerName: "Description",
    width: 250,
  },
  {
    field: "hostname",
    headerName: "Host",
    width: 200,
  },
  {
    field: "radioNode",
    headerName: "Radio node",
    width: 200,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "enabled",
    headerName: "Enabled",
    width: 120,
    sortable: false,
    align: "center",
    headerAlign: "center",
    renderCell: (cellValues: any) => {
      const enabled = cellValues.row.enabled;
      return enabled ? (
        <CheckCircleIcon sx={{ color: '#4ADE80', fontSize: 16 }} />
      ) : (
        <CancelIcon sx={{ color: '#94A3B8', fontSize: 16 }} />
      );
    },
  },
  {
    field: "state",
    headerName: "State",
    width: 130,
    renderCell: (cellValues: any) => (
      <Chip
        size="small"
        label={cellValues.row.state}
        sx={{
          backgroundColor: "var(--secondary)",
          color: "var(--secondary-foreground)",
          border: "none",
          fontWeight: "var(--font-weight-medium, 500)",
          fontSize: "var(--font-size-xs)",
          height: 26,
          borderRadius: "calc(var(--radius) - 2px)",
          "& .MuiChip-label": { px: 3 },
        }}
      />
    ),
  },
  {
    field: "zones",
    headerName: "Zones",
    width: 150,
    sortable: false,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "dlBandwidth",
    headerName: "DL bandwidth",
    width: 130,
    sortable: false,
    renderCell: (cellValues: any) => cellValues.value ? `${cellValues.value} MHz` : "",
  },
];

export const getUsersColumns = (
  handleEditUser: (user: IUser, isChangePass?: boolean) => void,
  handleDeleteUser: (user: IUser) => void
): GridColDef[] => [
    {
      field: "username",
      headerName: "User",
      flex: 1.5,
      minWidth: 150,
    },
    {
      field: "profile",
      headerName: "Profile",
      flex: 1,
      minWidth: 150,
      renderCell: (cellValues: any) => {
        const profileName = cellValues.row.profile?.name;
        return profileName ? <Badge variant="secondary">{profileName}</Badge> : "-";
      },
    },
    {
      field: "department",
      headerName: "Department",
      flex: 1.5,
      minWidth: 120,
    },
    {
      field: "location",
      headerName: "Location",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "phone",
      headerName: "Phone",
      flex: 1.5,
      minWidth: 130,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.5,
      minWidth: 150,
      renderCell: (cellValues: any) => {
        const email = cellValues.row.email;
        return email ? <a href={`mailto:${email}`} className="text-link hover:underline">{email}</a> : "-";
      },
    },
    {
      field: "lastLogIn",
      headerName: "Last Log in",
      flex: 1.5,
      minWidth: 150,
      renderCell: (cellValues: any) => {
        const date = cellValues.row.lastLoggedInTime || cellValues.row.currentLoggedInTime;
        return date ? dayjs(new Date(date)).format(DATEFORMATS.TSN) : "-";
      },
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      filterable: false,
      width: 50,
      renderCell: (cellValues: any) => {
        const user = cellValues.row;
        const isAdminUser = user.username?.toLowerCase() === "admin";

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 border-0 bg-transparent rounded-full text-foreground hover:bg-accent"
                aria-label="User actions"
              >
                <MoreVertical className="h-6 w-6 font-bold" strokeWidth={3} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  handleEditUser(user);
                }}
                onSelect={(e) => e.preventDefault()}
              >
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  handleEditUser(user, true);
                }}
                onSelect={(e) => e.preventDefault()}
              >
                Change Password
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleDeleteUser(user)}
                disabled={isAdminUser}
                className={isAdminUser ? "opacity-50 cursor-not-allowed text-red-500" : "text-red-500"}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];
